// @ts-nocheck
// profile-social.tsx — Perfil Social do Leitor (PF2). Header social + 4 abas
// (Visão geral / Biblioteca / Clubes / Conquistas) + sheets (pessoas, editar,
// bloquear/denunciar, compartilhar). Dados 100% reais via GET /api/users/:id.
// Próprio vs visitante; perfil privado (locked); estados loading/empty/erro.
//
// Port do design "Perfil do Leitor": as CSS vars do design (--cream/--card/...)
// são aliasadas pras vars reais do app (--ink/--paper-2/...) no container raiz,
// então o JSX do design roda quase verbatim e os tons batem com o resto do app.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../services/apiV2';
import { CoverCropper } from './clubs/cover-cropper';
import { StoriesProvider, StoriesOverlays, useStories } from './clubs/stories';
import { MentionInput } from './mention-input';
import { MentionText } from './mentions';
import { aoVoltarFechar } from './platform';

// ── Aliases de tema: design var -> var real do app ──────────────────────────
const THEME_ALIAS: any = {
  '--bg': 'var(--paper)', '--card': 'var(--paper-2)', '--card-2': 'var(--paper-3)',
  '--cream': 'var(--ink)', '--cream-2': 'var(--ink-2)', '--muted': 'var(--ink-3)',
  '--hair': 'var(--line, rgba(243,236,222,.09))',
  '--amber': 'var(--ember)', '--green': 'var(--sage)', '--purple': 'var(--plum)',
  '--lapis': 'var(--lapis)', '--danger': '#C03A2A',
  '--serif': "'Instrument Serif', Georgia, serif",
  '--sans': "'Geist', system-ui, -apple-system, sans-serif",
  '--mono': "'Geist Mono', ui-monospace, monospace",
};

// ── CSS local (animações + esconder scrollbar) injetado uma vez ─────────────
const PFS_CSS = `
@keyframes pfsUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
@keyframes pfsFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes pfsPop { from { transform: scale(.93); opacity: 0; } to { transform: scale(1); opacity: 1; } }
@keyframes pfsShim { 0%{opacity:.5} 50%{opacity:1} 100%{opacity:.5} }
.pfs-up { animation: pfsUp .32s cubic-bezier(.2,.8,.2,1); }
.pfs-fade { animation: pfsFade .2s ease; }
.pfs-pop { animation: pfsPop .22s cubic-bezier(.2,.8,.2,1); }
.pfs-shim { animation: pfsShim 1.4s ease-in-out infinite; }
.pfs-noscroll::-webkit-scrollbar { display: none; width: 0; height: 0; }
.pfs-em { font-style: italic; font-family: 'Instrument Serif', Georgia, serif; color: var(--ink); font-size: 1.05em; }
`;
let pfsCssInjected = false;
function injectCss() {
  if (pfsCssInjected || typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.id = 'pfs-css'; el.textContent = PFS_CSS;
  document.head.appendChild(el); pfsCssInjected = true;
}

// ── Ícones (port do design) ─────────────────────────────────────────────────
const I: any = {
  flame: 'M12 2c1.5 3 4 4.5 4 8a4 4 0 0 1-8 0c0-1 .3-1.8.7-2.5C8.2 7 9 4.5 12 2Zm0 18a3 3 0 0 1-3-3c0-1.6 1.2-2.6 3-5 1.8 2.4 3 3.4 3 5a3 3 0 0 1-3 3Z',
  moon: 'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z',
  book: 'M4 5a2 2 0 0 1 2-2h12v16H6a2 2 0 0 0-2 2V5Zm2 14h12M8 7h7M8 10h7',
  crown: 'M3 8l4 4 5-7 5 7 4-4-1.5 11h-15L3 8Z',
  target: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-4a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0-3a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  bolt: 'M13 2 4 14h6l-1 8 9-12h-6l1-8Z',
  globe: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0 0c2.5-2.3 4-5.6 4-9s-1.5-6.7-4-9c-2.5 2.3-4 5.6-4 9s1.5 6.7 4 9ZM3 12h18',
  stack: 'M12 3 3 8l9 5 9-5-9-5ZM3 13l9 5 9-5M3 17l9 5 9-5',
  chevron: 'M9 6l6 6-6 6',
  check: 'M5 12.5 10 17l9-10',
  users: 'M16 19v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2M9.5 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm11 10v-2a4 4 0 0 0-3-3.8M16 3.2A3 3 0 0 1 16 9',
  plus: 'M12 5v14M5 12h14',
  lock: 'M6 11V8a6 6 0 0 1 12 0v3M5 11h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Zm7 4v3',
  star: 'M12 3l2.6 5.6L21 9.3l-4.5 4.3L17.6 20 12 16.8 6.4 20l1.1-6.4L3 9.3l6.4-.7L12 3Z',
  shelf: 'M4 4h4v16H4V4Zm6 0h4v16h-4V4Zm7 .5 3.5 1-3 14.5-3.5-1L17 4.5Z',
  reading: 'M3 5c3-1.5 6-1.5 9 0v14c-3-1.5-6-1.5-9 0V5Zm9 0c3-1.5 6-1.5 9 0v14c-3-1.5-6-1.5-9 0',
  trophy: 'M7 4h10v3a5 5 0 0 1-10 0V4Zm0 1H4v1a3 3 0 0 0 3 3m10-4h3v1a3 3 0 0 1-3 3m-5 5v3m-3 3h6l-1-3H10l-1 3Z',
  gear: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm7.4-3a7.4 7.4 0 0 0-.1-1.2l2-1.6-2-3.4-2.4 1a7.3 7.3 0 0 0-2-1.2L14.5 2h-4l-.4 2.6a7.3 7.3 0 0 0-2 1.2l-2.4-1-2 3.4 2 1.6a7.4 7.4 0 0 0 0 2.4l-2 1.6 2 3.4 2.4-1a7.3 7.3 0 0 0 2 1.2l.4 2.6h4l.4-2.6a7.3 7.3 0 0 0 2-1.2l2.4 1 2-3.4-2-1.6c.06-.4.1-.8.1-1.2Z',
  share: 'M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7M16 6l-4-4-4 4M12 2.5v13',
  edit: 'M4 20h4L19 9l-4-4L4 16v4ZM14 6l4 4',
  back: 'M15 6l-6 6 6 6',
  pages2: 'M5 4h9l5 5v11H5V4Zm9 0v5h5',
  dots: 'M5 12h.01M12 12h.01M19 12h.01',
  block: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM5.6 5.6l12.8 12.8',
  flag: 'M5 21v-8m0 0V4.5c2.5-1.6 5 1.4 7.5 0S18 4 18 4v8s-3 1.5-5.5 0S5 13 5 13Z',
  sun: 'M12 4V2m0 20v-2m8-8h2M2 12h2m13.6-5.6 1.4-1.4M5 19l1.4-1.4m0-11.2L5 5m14 14-1.4-1.4M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z',
  run: 'M13 5a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm-1 3 4 2 1 4m-9 6 2-5 3-2-4-3-3 2-2 3',
  pen: 'M4 20h4L18 10l-4-4L4 16v4ZM13 7l4 4',
  close: 'M6 6l12 12M18 6 6 18',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-13v5l3 2',
  camera: 'M4 7a2 2 0 0 1 2-2h2l1.2-1.6a1 1 0 0 1 .8-.4h4a1 1 0 0 1 .8.4L18 5h2a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7Zm8 3a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Z',
};
function Icon({ name, size = 22, stroke = 2, fill = false, style, color }: any) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ color, ...style }} aria-hidden="true">
      <path d={I[name] || ''} fill={fill ? 'currentColor' : 'none'} stroke="currentColor"
        strokeWidth={fill ? 0 : stroke} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────
const TINTS = [['#E87A3B', '#C08CAE'], ['#7FA8A6', '#9EB083'], ['#C08CAE', '#E87A3B'], ['#E8C77B', '#E87A3B'], ['#9EB083', '#7FA8A6'], ['#7FA8A6', '#C08CAE'], ['#C08CAE', '#7FA8A6']];
function tintOf(seed: string) {
  let h = 0; const s = String(seed || '?');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return TINTS[h % TINTS.length];
}
const PLAN_LABEL: any = { free: null, reader: 'READER', reader_plus: 'READER+', leader: 'LÍDER', lider: 'LÍDER', leader_pro: 'LÍDER PRO', lider_pro: 'LÍDER PRO' };
function planLabel(p: string) { return PLAN_LABEL[String(p || '').toLowerCase()] ?? null; }

function relTime(iso: string) {
  if (!iso) return '';
  const then = new Date(iso).getTime(); if (isNaN(then)) return '';
  const diff = Date.now() - then; const day = 86400000;
  if (diff < 0) return 'agora';
  if (diff < 3600000) { const m = Math.max(1, Math.round(diff / 60000)); return `há ${m} min`; }
  if (diff < day) { const h = Math.round(diff / 3600000); return `há ${h}h`; }
  const d = Math.round(diff / day);
  if (d === 1) return 'ontem';
  if (d < 7) return `há ${d} dias`;
  if (d < 30) { const w = Math.round(d / 7); return `há ${w} semana${w > 1 ? 's' : ''}`; }
  if (d < 365) { const mo = Math.round(d / 30); return `há ${mo} ${mo > 1 ? 'meses' : 'mês'}`; }
  const y = Math.round(d / 365); return `há ${y} ano${y > 1 ? 's' : ''}`;
}
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
function fmtDate(iso: string) {
  if (!iso) return ''; const d = new Date(iso); if (isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
function sinceLabel(iso: string) {
  if (!iso) return ''; const d = new Date(iso); if (isNaN(d.getTime())) return '';
  const full = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
  return `${full[d.getMonth()]} de ${d.getFullYear()}`;
}
function hueGradient(hue: number) {
  const h = Number.isFinite(hue) ? hue : 24;
  return `linear-gradient(150deg, oklch(0.55 0.11 ${h}), oklch(0.30 0.09 ${h}))`;
}
function statusKey(b: any) { return b.status === 'lendo' ? 'lendo' : b.status === 'lido' ? 'lido' : 'quero'; }
function fmtNum(n: number) { return (n >= 1000 ? (n / 1000).toFixed(1).replace('.', ',') + 'k' : String(n || 0)); }

// ── Botões ──────────────────────────────────────────────────────────────────
function BtnAmber({ children, onClick, style, disabled }: any) {
  return <button onClick={onClick} disabled={disabled} style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: disabled ? 'default' : 'pointer',
    fontFamily: 'var(--sans)', fontSize: 13.5, fontWeight: 600, padding: '9px 16px', borderRadius: 11, border: 'none', whiteSpace: 'nowrap',
    background: 'linear-gradient(135deg, var(--ember), #E8A35B)', color: '#231007', boxShadow: '0 4px 16px rgba(232,122,59,.3)', opacity: disabled ? 0.55 : 1, ...style,
  }}>{children}</button>;
}
function BtnGhost({ children, onClick, style }: any) {
  return <button onClick={onClick} style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer',
    fontFamily: 'var(--sans)', fontSize: 13.5, fontWeight: 600, padding: '9px 16px', borderRadius: 11,
    background: 'var(--card-2)', color: 'var(--cream)', border: '1px solid var(--hair)', whiteSpace: 'nowrap', ...style,
  }}>{children}</button>;
}

// ── Avatar / PlanBadge ──────────────────────────────────────────────────────
function Avatar({ name, tint, size = 56, ring = false, url }: any) {
  const t = tint || tintOf(name);
  const initial = (name || '?').trim()[0]?.toUpperCase() || '?';
  const shadow = ring ? '0 0 0 3px var(--bg), 0 4px 14px rgba(0,0,0,.4)' : '0 2px 8px rgba(0,0,0,.3)';
  const border = ring ? 'none' : '1px solid rgba(255,255,255,.12)';
  if (url) return (
    <div style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, overflow: 'hidden', boxShadow: shadow, border, background: '#1a1512' }}>
      <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    </div>
  );
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: `linear-gradient(140deg, ${t[0]}, ${t[1]})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--serif)', color: '#fff', fontSize: size * 0.42, lineHeight: 1,
      boxShadow: shadow, border, textShadow: '0 1px 2px rgba(0,0,0,.25)', userSelect: 'none',
    }}>{initial}</div>
  );
}
function PlanBadge({ plan }: any) {
  if (!plan) return null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, background: 'linear-gradient(135deg, #E87A3B, #E8A35B)',
      color: '#231007', fontFamily: 'var(--mono)', fontWeight: 600, fontSize: 10, letterSpacing: '.1em',
      padding: '4px 9px 4px 7px', borderRadius: 999, boxShadow: '0 2px 10px rgba(232,122,59,.35)', whiteSpace: 'nowrap',
    }}><Icon name="star" size={11} fill />{plan}</span>
  );
}

// ── Capa de livro real (imagem ou gradiente de tom) ─────────────────────────
function BookCover({ book, width = 116 }: any) {
  const h = width * 1.5;
  const base: any = { position: 'relative', width, height: h, borderRadius: 5, overflow: 'hidden', flexShrink: 0, boxShadow: '0 6px 18px rgba(0,0,0,.45), inset 0 0 0 1px rgba(255,255,255,.06)' };
  // Capa que falha cai no quadro de tom com o título (pente fino, UX-32).
  const [falhou, setFalhou] = useState(false);
  useEffect(() => { setFalhou(false); }, [book.coverUrl]);
  if (book.coverUrl && !falhou) {
    return <div style={base}><img src={book.coverUrl} alt={book.title} onError={() => setFalhou(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></div>;
  }
  return (
    <div style={{ ...base, background: hueGradient(book.coverHue), color: '#F3ECDE', fontFamily: 'var(--serif)' }}>
      <div style={{ position: 'absolute', inset: 0, padding: width * 0.1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
        <div style={{ width: width * 0.28, height: 2, background: 'rgba(255,255,255,.6)', marginBottom: width * 0.07 }} />
        <div style={{ fontSize: Math.max(12, width * 0.13), lineHeight: 1.04, fontWeight: 500 }}>{book.title}</div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: Math.max(7, width * 0.056), letterSpacing: '.08em', textTransform: 'uppercase', color: 'rgba(243,236,222,.62)', marginTop: width * 0.05 }}>{book.author}</div>
      </div>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 55%, rgba(0,0,0,.35))', pointerEvents: 'none' }} />
    </div>
  );
}

// ── Section head / Card / EmptyState ────────────────────────────────────────
function SectionHead({ eyebrow, title, action, onAction }: any) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 13 }}>
      <div>
        {eyebrow && <div style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 5 }}>{eyebrow}</div>}
        <h2 style={{ fontFamily: 'var(--serif)', fontSize: 22, lineHeight: 1, margin: 0, color: 'var(--cream)' }}>{title}</h2>
      </div>
      {action && <button onClick={onAction} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--amber)', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 2, fontFamily: 'var(--sans)' }}>{action} <Icon name="chevron" size={15} /></button>}
    </div>
  );
}
function Card({ children, style, glow, onClick }: any) {
  return <div onClick={onClick} style={{ background: 'var(--card)', border: '1px solid var(--hair)', borderRadius: 16, boxShadow: glow ? '0 0 40px rgba(232,122,59,.10), 0 4px 18px rgba(0,0,0,.3)' : '0 2px 12px rgba(0,0,0,.22)', cursor: onClick ? 'pointer' : 'default', ...style }}>{children}</div>;
}
function EmptyState({ icon, title, body, action, onAction }: any) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 26px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ width: 64, height: 64, borderRadius: 18, background: 'var(--card-2)', border: '1px solid var(--hair)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--amber)', marginBottom: 18 }}><Icon name={icon} size={28} /></div>
      <h3 style={{ fontFamily: 'var(--serif)', fontSize: 22, margin: '0 0 8px', color: 'var(--cream)' }}>{title}</h3>
      <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--muted)', margin: 0, maxWidth: 280 }}>{body}</p>
      {action && <div style={{ marginTop: 20 }}><BtnAmber onClick={onAction}>{action}</BtnAmber></div>}
    </div>
  );
}

// ── Stat panel + heatmap (dados reais) ──────────────────────────────────────
function StatBig({ value, label, sub, color }: any) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontFamily: 'var(--serif)', fontSize: 32, lineHeight: .95, color: color || 'var(--cream)' }}>{value}</div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 9.5, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--muted)', marginTop: 6 }}>{label}</div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--cream-2)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}
function buildHeatGrid(heatmap: any) {
  const weeks = 24, days = weeks * 7;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  // alinha o fim na coluna atual; volta `days-1` dias
  const start = new Date(today); start.setDate(start.getDate() - (days - 1));
  const lvlOf = (p: number) => p <= 0 ? 0 : p < 10 ? 1 : p < 25 ? 2 : p < 50 ? 3 : 4;
  const grid: number[][] = []; for (let r = 0; r < 7; r++) grid.push(new Array(weeks).fill(0));
  for (let i = 0; i < days; i++) {
    const d = new Date(start); d.setDate(start.getDate() + i);
    const key = d.toISOString().slice(0, 10);
    const w = Math.floor(i / 7), r = i % 7;
    grid[r][w] = lvlOf((heatmap && heatmap[key]) || 0);
  }
  return grid;
}
function Heatmap({ grid }: any) {
  const colors = ['rgba(243,236,222,.05)', 'rgba(158,176,131,.35)', 'rgba(158,176,131,.55)', 'rgba(232,163,91,.7)', 'var(--amber)'];
  return (
    <div>
      <div style={{ display: 'flex', gap: 3, justifyContent: 'space-between' }}>
        {grid[0].map((_: any, w: number) => (
          <div key={w} style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
            {grid.map((row: number[], r: number) => <div key={r} style={{ width: '100%', aspectRatio: '1', borderRadius: 2.5, background: colors[row[w]] }} />)}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 5, marginTop: 9 }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--muted)', letterSpacing: '.1em' }}>MENOS</span>
        {colors.map((c, i) => <div key={i} style={{ width: 10, height: 10, borderRadius: 2.5, background: c }} />)}
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--muted)', letterSpacing: '.1em' }}>MAIS</span>
      </div>
    </div>
  );
}
function StatPanel({ stats }: any) {
  const grid = buildHeatGrid(stats.heatmap);
  const hasHeat = grid.some((row: number[]) => row.some((v) => v > 0));
  return (
    <Card style={{ padding: 18 }}>
      <div style={{ display: 'flex', gap: 14 }}>
        <StatBig value={stats.streak} label="Sequência" sub={stats.streak === 1 ? 'dia' : 'dias seguidos'} color="var(--amber)" />
        <div style={{ width: 1, background: 'var(--hair)' }} />
        <StatBig value={stats.booksYear} label="Lidos / ano" sub={`${stats.booksTotal} no total`} />
        <div style={{ width: 1, background: 'var(--hair)' }} />
        <StatBig value={(stats.pages || 0).toLocaleString('pt-BR')} label="Páginas" sub={`≈ ${stats.hours}h`} />
      </div>
      {hasHeat && (
        <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--hair)' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 11 }}>Últimas 24 semanas</div>
          <Heatmap grid={grid} />
        </div>
      )}
    </Card>
  );
}

// ── Club card (dados reais) ─────────────────────────────────────────────────
const ROLE_LABEL: any = { owner: 'Líder', admin: 'Admin', moderator: 'Moderador', member: 'Membro' };
function ClubCard({ club, wide, onClick }: any) {
  const lead = club.role === 'owner' || club.role === 'admin';
  const roleTxt = ROLE_LABEL[club.role] || 'Membro';
  const glyph = (club.name || '?').trim()[0]?.toUpperCase() || '?';
  return (
    <div onClick={onClick} style={{
      position: 'relative', borderRadius: 16, overflow: 'hidden', flexShrink: 0,
      width: wide ? '100%' : 158, border: '1px solid var(--hair)', cursor: 'pointer',
      background: club.customCoverUrl ? '#1a1512' : hueGradient(club.hue),
    }}>
      {club.customCoverUrl && <img src={club.customCoverUrl} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,.18) 20%, rgba(0,0,0,.62))' }} />
      {!club.customCoverUrl && <div style={{ position: 'absolute', right: -8, top: -14, fontFamily: 'var(--serif)', fontSize: 96, lineHeight: 1, color: 'rgba(255,255,255,.16)' }}>{glyph}</div>}
      <div style={{ position: 'relative', padding: wide ? 16 : 13, height: wide ? 'auto' : 150, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9.5, letterSpacing: '.12em', textTransform: 'uppercase', padding: '4px 8px', borderRadius: 999, background: lead ? 'var(--amber)' : 'rgba(0,0,0,.4)', color: lead ? '#231007' : 'var(--cream-2)', border: lead ? 'none' : '1px solid rgba(255,255,255,.16)' }}>{roleTxt}</div>
        </div>
        <div>
          <div style={{ fontFamily: 'var(--serif)', fontSize: wide ? 21 : 17.5, lineHeight: 1.05, color: '#F8F2E6', marginBottom: 6 }}>{club.name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: 'rgba(243,236,222,.7)', fontFamily: 'var(--mono)', letterSpacing: '.04em' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="users" size={13} /> {fmtNum(club.members)}</span>
            <span style={{ opacity: .6 }}>· {club.mode === 'group-read' ? 'leitura' : 'comunidade'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Achievement badge (dados reais) ─────────────────────────────────────────
const ACH_TINTS = ['#E87A3B', '#7FA8A6', '#C08CAE', '#E8C77B', '#9EB083', '#E87A3B', '#7FA8A6', '#C08CAE'];
function AchievementBadge({ a, idx = 0, compact }: any) {
  const locked = !a.unlocked; const tint = ACH_TINTS[idx % ACH_TINTS.length];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 9, opacity: locked ? .42 : 1, filter: locked ? 'grayscale(.7)' : 'none' }}>
      <div style={{
        position: 'relative', width: compact ? 62 : 72, height: compact ? 62 : 72, borderRadius: '50%',
        background: locked ? 'var(--card-2)' : `radial-gradient(circle at 35% 30%, ${tint}, ${tint}99)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: locked ? 'var(--muted)' : '#fff',
        boxShadow: locked ? 'inset 0 0 0 1px var(--hair)' : `0 6px 20px ${tint}44, inset 0 2px 6px rgba(255,255,255,.3)`, border: locked ? '1px dashed var(--hair)' : 'none',
      }}>
        <Icon name={locked ? 'lock' : (I[a.icon] ? a.icon : 'trophy')} size={compact ? 26 : 30} fill={!locked && ['flame', 'star'].includes(a.icon)} />
        {!locked && <div style={{ position: 'absolute', inset: -4, borderRadius: '50%', border: `1.5px solid ${tint}55` }} />}
      </div>
      <div>
        <div style={{ fontFamily: 'var(--serif)', fontSize: compact ? 14 : 15, color: 'var(--cream)', lineHeight: 1.1 }}>{a.name}</div>
        {!compact && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3, lineHeight: 1.3 }}>{a.desc}</div>}
      </div>
    </div>
  );
}

// ── Header ──────────────────────────────────────────────────────────────────
function Chip({ icon, iconColor, children }: any) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--card-2)', border: '1px solid var(--hair)', borderRadius: 999, padding: '6px 11px 6px 9px', fontSize: 12.5, color: 'var(--cream-2)', whiteSpace: 'nowrap' }}>
      {icon && <Icon name={icon} size={14} color={iconColor || 'var(--amber)'} fill={icon === 'flame'} />}{children}
    </div>
  );
}
function StatCount({ label, value, onClick, accent, small }: any) {
  return (
    <button onClick={onClick} style={{ background: 'none', border: 'none', padding: 0, cursor: onClick ? 'pointer' : 'default', textAlign: 'left', color: 'inherit', display: small ? 'flex' : 'flex', flexDirection: small ? 'row' : 'column', alignItems: small ? 'baseline' : 'flex-start', gap: small ? 5 : 2 }}>
      <span style={{ fontFamily: 'var(--serif)', fontSize: small ? 13 : 19, lineHeight: 1, color: accent ? 'var(--amber)' : (small ? 'var(--muted)' : 'var(--cream)') }}>{value}</span>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '.1em', textTransform: 'uppercase', color: accent ? 'var(--amber)' : 'var(--muted)' }}>{label}</span>
    </button>
  );
}
function ProfileHeader({ data, onBack, onOpenPeople, onOpenSettings, onOpenMore, onOpenRequests, onEdit, onShare, onFollow, followBusy, stories, onOpenStories }: any) {
  const isMe = data.isMe; const myPrivate = isMe && data.isPrivate; const tint = tintOf(data.id);
  const pl = planLabel(data.plan);
  return (
    <div style={{ position: 'relative', padding: '16px 18px 4px' }}>
      {onBack && (
        <button onClick={onBack} aria-label="Voltar" style={{ position: 'absolute', top: 'calc(10px + env(safe-area-inset-top,0px))', left: 12, width: 36, height: 36, borderRadius: 10, border: '1px solid var(--hair)', background: 'var(--card-2)', color: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 2 }}><Icon name="back" size={18} /></button>
      )}
      <div style={{ display: 'flex', gap: 16, marginTop: onBack ? 40 : 0 }}>
        {(stories && stories.length > 0) ? (
          <button onClick={onOpenStories} aria-label="Ver stories" style={{ flexShrink: 0, padding: 3, borderRadius: '50%', border: 'none', cursor: 'pointer', lineHeight: 0, background: (!data.isMe && stories.every((st: any) => st.seenByMe)) ? 'var(--hair)' : 'linear-gradient(135deg, var(--amber), #E8A35B)' }}>
            <div style={{ padding: 3, borderRadius: '50%', background: 'var(--paper)', lineHeight: 0 }}>
              <Avatar name={data.name} tint={tint} size={82} url={data.avatarUrl} />
            </div>
          </button>
        ) : (
          <Avatar name={data.name} tint={tint} size={94} url={data.avatarUrl} />
        )}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ flex: 1, minWidth: 0, fontFamily: 'var(--serif)', fontSize: 23, lineHeight: 1.08, margin: 0, color: 'var(--cream)' }}>{data.name}</h1>
            {isMe
              ? <button onClick={onOpenSettings} aria-label="Ajustes" style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 10, border: '1px solid var(--hair)', background: 'var(--card-2)', color: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><Icon name="gear" size={20} /></button>
              : <button onClick={onOpenMore} aria-label="Mais opções" style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 10, border: '1px solid var(--hair)', background: 'var(--card-2)', color: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><Icon name="dots" size={20} /></button>}
          </div>
          {data.username && <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)' }}>@{data.username}</div>}
          {/* AUDITORIA SOCIAL (protocolo, correção 5) — o lugar de destaque é da
              EXECUÇÃO, não da audiência. "Livros" contava livros cadastrados, o
              que inclui intenção; agora o topo mostra lidos e dias de leitura,
              que só existem com página virada. Seguidores e seguindo continuam
              acessíveis, um degrau abaixo. */}
          <div style={{ display: 'flex', gap: 18, marginTop: 8 }}>
            <StatCount label="Lidos" value={(data.stats && data.stats.booksRead) || 0} />
            <StatCount label={((data.stats && data.stats.streak) || 0) === 1 ? 'Dia lendo' : 'Dias lendo'} value={(data.stats && data.stats.streak) || 0} />
            {data.isMe && data.pendingRequests > 0 && <StatCount label="Pedidos" value={data.pendingRequests} accent onClick={onOpenRequests} />}
          </div>
          <div style={{ display: 'flex', gap: 14, marginTop: 6 }}>
            <StatCount label="Seguidores" value={data.followers} small onClick={data.locked ? undefined : () => onOpenPeople('followers')} />
            <StatCount label="Seguindo" value={data.following} small onClick={data.locked ? undefined : () => onOpenPeople('following')} />
          </div>
        </div>
      </div>

      {data.bio && <p style={{ fontSize: 13.5, lineHeight: 1.45, color: 'var(--cream-2)', margin: '14px 0 0' }}><MentionText text={data.bio} /></p>}

      <div style={{ display: 'flex', gap: 7, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {myPrivate && <Chip icon="lock" iconColor="var(--muted)">Privado</Chip>}
        {!isMe && data.followsYou && <Chip icon="users" iconColor="var(--lapis)">Segue você</Chip>}
        {data.stats && <Chip icon="flame" iconColor="var(--amber)">{data.stats.streak} {data.stats.streak === 1 ? 'dia' : 'dias'}</Chip>}
        {data.stats && <Chip icon="book" iconColor="var(--green)">{data.stats.booksRead} lidos</Chip>}
        {pl && <PlanBadge plan={pl} />}
      </div>

      {!isMe && data.common && (
        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 10, background: 'linear-gradient(120deg, rgba(232,122,59,.12), rgba(192,140,174,.08))', border: '1px solid rgba(232,122,59,.22)' }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(232,122,59,.16)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--amber)', flexShrink: 0 }}><Icon name="users" size={15} /></div>
          <div style={{ fontSize: 12.5, lineHeight: 1.4, color: 'var(--cream-2)' }}>
            <strong style={{ color: 'var(--cream)', fontWeight: 600 }}>{data.common.clubs} {data.common.clubs === 1 ? 'clube em comum' : 'clubes em comum'}</strong>{data.common.clubName ? <> — incluindo <span style={{ color: 'var(--amber)' }}>{data.common.clubName}</span></> : null}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        {isMe ? (
          <>
            <BtnGhost onClick={onEdit} style={{ flex: 1 }}><Icon name="edit" size={15} /> Editar perfil</BtnGhost>
            <BtnGhost onClick={onShare} style={{ flex: 1 }}><Icon name="share" size={15} /> Compartilhar</BtnGhost>
          </>
        ) : (
          <>
            {data.isFollowing
              ? <BtnGhost onClick={onFollow} style={{ flex: 1, opacity: followBusy ? 0.6 : 1 }}><Icon name="check" size={15} /> Seguindo</BtnGhost>
              : data.requested
                ? <BtnGhost onClick={onFollow} style={{ flex: 1, opacity: followBusy ? 0.6 : 1 }}><Icon name="clock" size={15} /> Solicitado</BtnGhost>
                : <BtnAmber onClick={onFollow} disabled={followBusy} style={{ flex: 1 }}><Icon name="plus" size={15} /> Seguir</BtnAmber>}
            <BtnGhost onClick={onShare} style={{ flex: 1 }}><Icon name="share" size={15} /> Compartilhar</BtnGhost>
          </>
        )}
      </div>
    </div>
  );
}
function TabBar({ tabs, active, onChange }: any) {
  return (
    <div style={{ position: 'sticky', top: 0, zIndex: 20, background: 'color-mix(in srgb, var(--paper) 88%, transparent)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', borderBottom: '1px solid var(--hair)', marginTop: 14 }}>
      <div className="pfs-noscroll" style={{ display: 'flex', gap: 2, padding: '0 12px', overflowX: 'auto', scrollbarWidth: 'none' }}>
        {tabs.map((t: any) => {
          const on = t.id === active;
          return (
            <button key={t.id} onClick={() => onChange(t.id)} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', padding: '13px 11px 12px', whiteSpace: 'nowrap', flexShrink: 0, fontFamily: 'var(--sans)', fontSize: 13.5, fontWeight: on ? 600 : 500, color: on ? 'var(--cream)' : 'var(--muted)' }}>
              {t.label}
              {/* A seta avisa que esta aba ABRE OUTRA TELA em vez de trocar o
                  painel abaixo. Sem ela, a aba prometeria ficar acesa e não
                  ficaria — a pessoa toparia com outra tela sem entender. */}
              {t.leva && <Icon name="chevron-right" size={11} color="var(--muted)" />}
              <span style={{ position: 'absolute', left: 8, right: 8, bottom: 0, height: 2.5, borderRadius: 3, background: on ? 'var(--amber)' : 'transparent' }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Abas ────────────────────────────────────────────────────────────────────
function LockedCard({ name, onFollow }: any) {
  return <Card style={{ padding: 0, overflow: 'hidden', marginTop: 8 }}><EmptyState icon="lock" title="Este perfil é privado" body={`Siga ${(name || '').split(' ')[0]} para ver a estante, os clubes e a atividade de leitura.`} action="Seguir" onAction={onFollow} /></Card>;
}
function OverviewTab({ data, friends, onTab, onOpenClub, onOpenProfile, onUpgrade, onFollow, onOpenBook }: any) {
  if (data.locked) return <div style={{ padding: '8px 0' }}><LockedCard name={data.name} onFollow={onFollow} /></div>;
  const books = data.books || []; const clubs = data.clubs || []; const achs = data.achievements || [];
  const featured = [...books.filter((b: any) => b.status === 'lendo'), ...books.filter((b: any) => b.status !== 'lendo')].slice(0, 8);
  const unlocked = achs.filter((a: any) => a.unlocked);
  const isFree = data.isMe && !planLabel(data.plan);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 30, padding: '6px 0 8px' }}>
      {featured.length > 0 && (
        <div>
          <SectionHead eyebrow="A estante" title="Em destaque" action="Ver tudo" onAction={() => onTab('biblioteca')} />
          <div className="pfs-noscroll" style={{ display: 'flex', gap: 13, overflowX: 'auto', padding: '2px 18px 6px', margin: '0 -18px', scrollbarWidth: 'none' }}>
            {featured.map((b: any) => {
              const pct = b.status === 'lendo' && b.totalPages ? Math.min(100, Math.round((b.currentPage / b.totalPages) * 100)) : 0;
              return (
                <div key={b.id} onClick={() => onOpenBook && b.id && onOpenBook(b.id)} style={{ flexShrink: 0, width: 116, cursor: onOpenBook ? 'pointer' : 'default' }}>
                  <BookCover book={b} width={116} />
                  {b.status === 'lendo' ? (
                    <div style={{ marginTop: 9 }}>
                      <div style={{ height: 4, borderRadius: 3, background: 'var(--card-2)', overflow: 'hidden' }}><div style={{ width: `${pct}%`, height: '100%', background: 'var(--amber)' }} /></div>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--amber)', marginTop: 5 }}>{pct}% · LENDO</div>
                    </div>
                  ) : <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--muted)', marginTop: 9, letterSpacing: '.06em' }}>{b.status === 'lido' ? 'LIDO' : 'QUERO LER'}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.stats && (
        <div>
          <SectionHead eyebrow="Números de leitura" title="Em ritmo" />
          <StatPanel stats={data.stats} />
        </div>
      )}

      {isFree && (
        <div onClick={onUpgrade} style={{ position: 'relative', overflow: 'hidden', borderRadius: 18, padding: 20, cursor: 'pointer', background: 'linear-gradient(135deg, #2a1c12, #1d1512)', border: '1px solid rgba(232,122,59,.3)', boxShadow: '0 0 50px rgba(232,122,59,.14)' }}>
          <div style={{ position: 'absolute', right: -30, top: -30, width: 140, height: 140, borderRadius: '50%', background: 'radial-gradient(circle, rgba(232,122,59,.4), transparent 70%)' }} />
          <div style={{ position: 'relative' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--amber)', marginBottom: 10 }}>Forjamente · planos</div>
            <h3 style={{ fontFamily: 'var(--serif)', fontSize: 27, margin: '0 0 6px', color: 'var(--cream)' }}>Escolha seu ritmo</h3>
            <p style={{ fontSize: 13.5, color: 'var(--cream-2)', margin: '0 0 16px' }}>Insights, metas e clubes ilimitados</p>
            <BtnAmber style={{ display: 'inline-flex' }}>Conhecer planos <Icon name="chevron" size={15} /></BtnAmber>
          </div>
        </div>
      )}

      {clubs.length > 0 && (
        <div>
          <SectionHead eyebrow="Comunidade" title="Clubes" action="Ver todos" onAction={() => onTab('clubes')} />
          <div className="pfs-noscroll" style={{ display: 'flex', gap: 12, overflowX: 'auto', padding: '2px 18px 6px', margin: '0 -18px', scrollbarWidth: 'none' }}>
            {clubs.map((c: any) => <ClubCard key={c.id} club={c} onClick={() => onOpenClub && onOpenClub(c.id)} />)}
          </div>
        </div>
      )}

      {unlocked.length > 0 && (
        <div>
          <SectionHead eyebrow="Vitrine" title="Conquistas" action="Ver todas" onAction={() => onTab('conquistas')} />
          <div className="pfs-noscroll" style={{ display: 'flex', gap: 16, overflowX: 'auto', padding: '4px 18px 8px', margin: '0 -18px', scrollbarWidth: 'none' }}>
            {unlocked.map((a: any, i: number) => <div key={a.id} style={{ flexShrink: 0, width: 92 }}><AchievementBadge a={a} idx={achs.indexOf(a)} compact /></div>)}
          </div>
        </div>
      )}

      {data.isMe && friends && friends.length > 0 && (
        <div>
          <SectionHead eyebrow="Quem você segue" title="Andam lendo" />
          <FriendsStrip items={friends} onOpenProfile={onOpenProfile} />
        </div>
      )}
    </div>
  );
}

const ACT_META: any = {
  finished: { icon: 'check', tint: '#9EB083' },
  club: { icon: 'users', tint: '#C08CAE' },
  badge: { icon: 'trophy', tint: '#E87A3B' },
};
function ActivityFeed({ items }: any) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {items.map((it: any, i: number) => {
        const m = ACT_META[it.type] || ACT_META.badge; const last = i === items.length - 1;
        let text: any;
        if (it.type === 'finished') text = <>Terminou <span className="pfs-em">{it.book?.title}</span></>;
        else if (it.type === 'club') text = <>Entrou no clube <span className="pfs-em">{it.clubName}</span></>;
        else text = <>Desbloqueou <span className="pfs-em">{it.badgeName || it.badge}</span></>;
        return (
          <div key={i} style={{ display: 'flex', gap: 13, position: 'relative' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: `${m.tint}22`, color: m.tint, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}><Icon name={m.icon} size={17} fill={it.type === 'badge'} /></div>
              {!last && <div style={{ width: 1.5, flex: 1, background: 'var(--hair)', marginTop: 2 }} />}
            </div>
            <div style={{ paddingBottom: last ? 0 : 18, flex: 1 }}>
              <div style={{ fontSize: 14, lineHeight: 1.45, color: 'var(--cream-2)' }}>{text}</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.08em', color: 'var(--muted)', marginTop: 3 }}>{relTime(it.when)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


/* ── Estantes, editáveis onde elas são vistas ────────────────────────────────
   A edição morava numa tela de ajustes chamada "Minha estante", longe do lugar
   onde as estantes aparecem. Quem quisesse criar uma tinha que sair do perfil,
   entrar em ajustes e voltar — e vincular um livro exigia abrir o livro, achar
   o menu "⋯" e marcar um chip.

   Agora tudo acontece aqui: criar, renomear, reordenar, excluir e escolher os
   livros de cada estante. Só para o dono do perfil (`isMe`), nunca no de
   outra pessoa.                                                              */

// Sheet de criar/renomear. Um campo, e o emoji é opcional porque a coluna já
// existia e nunca teve como preencher.
function EstanteSheet({ inicial, onSalvar, onFechar }: any) {
  const [nome, setNome] = useState(inicial?.name || '');
  const [emoji, setEmoji] = useState(inicial?.emoji || '');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const editando = !!inicial?.id;

  async function salvar() {
    const n = nome.trim();
    if (!n || salvando) return;
    setSalvando(true); setErro(null);
    try { await onSalvar({ name: n, emoji: emoji.trim() || null }); onFechar(); }
    catch (e: any) { setErro(e?.message || 'Não consegui salvar.'); }
    finally { setSalvando(false); }
  }

  return (
    <SheetWrap onClose={onFechar} maxHeight="62%">
      <div style={{ padding: '10px 20px 22px' }}>
        <h3 style={{ fontFamily: 'var(--serif)', fontSize: 23, margin: '6px 0 4px', color: 'var(--cream)' }}>
          {editando ? 'Renomear estante' : 'Nova estante'}
        </h3>
        <p style={{ fontSize: 13.5, color: 'var(--muted)', margin: '0 0 18px', lineHeight: 1.5 }}>
          {editando ? 'O nome muda em todo lugar; os livros continuam onde estão.' : 'Um nome que faça sentido pra você — "Clássicos", "Pra reler", "Do trabalho".'}
        </p>
        <div style={{ display: 'flex', gap: 9 }}>
          <input
            value={emoji} onChange={(e) => setEmoji(e.target.value.slice(0, 4))}
            placeholder="🙂" aria-label="Emoji da estante"
            style={{ width: 58, textAlign: 'center', padding: '13px 0', borderRadius: 13, border: '1px solid var(--hair)', background: 'var(--card-2)', color: 'var(--cream)', fontSize: 19, outline: 'none' }}
          />
          <input
            value={nome} onChange={(e) => setNome(e.target.value.slice(0, 60))} autoFocus
            placeholder="Nome da estante"
            onKeyDown={(e: any) => { if (e.key === 'Enter') salvar(); }}
            style={{ flex: 1, minWidth: 0, padding: '13px 15px', borderRadius: 13, border: '1px solid var(--hair)', background: 'var(--card-2)', color: 'var(--cream)', fontSize: 15.5, outline: 'none', fontFamily: 'inherit' }}
          />
        </div>
        {erro && <div style={{ fontSize: 13, color: 'var(--danger)', marginTop: 12 }}>{erro}</div>}
        <button onClick={salvar} disabled={!nome.trim() || salvando} style={{
          width: '100%', marginTop: 18, padding: '15px 20px', borderRadius: 999, border: 'none',
          background: nome.trim() ? 'var(--amber)' : 'var(--card-2)',
          color: nome.trim() ? '#1a120c' : 'var(--muted)',
          fontSize: 15, fontWeight: 700, cursor: nome.trim() ? 'pointer' : 'default',
        }}>{salvando ? 'um instante…' : (editando ? 'Salvar' : 'Criar estante')}</button>
      </div>
    </SheetWrap>
  );
}

// Escolher os livros de UMA estante. Marca e desmarca; salva no fechar, com o
// diff — assim marcar cinco livros é uma leva de chamadas, não cinco idas e
// vindas com a lista pulando na tela.
function LivrosDaEstanteSheet({ estante, livros, onSalvar, onFechar }: any) {
  const [sel, setSel] = useState<any>(() => {
    const m: any = {};
    (livros || []).forEach((b: any) => { m[b.id] = (b.shelfIds || []).includes(estante.id); });
    return m;
  });
  const [busca, setBusca] = useState('');
  const [salvando, setSalvando] = useState(false);

  const visiveis = (livros || []).filter((b: any) => {
    const q = busca.trim().toLowerCase();
    if (!q) return true;
    return (b.title || '').toLowerCase().includes(q) || (b.author || '').toLowerCase().includes(q);
  });
  const marcados = Object.values(sel).filter(Boolean).length;

  async function salvar() {
    if (salvando) return;
    setSalvando(true);
    try {
      const antes: any = {};
      (livros || []).forEach((b: any) => { antes[b.id] = (b.shelfIds || []).includes(estante.id); });
      const entrar = Object.keys(sel).filter((id) => sel[id] && !antes[id]);
      const sair = Object.keys(antes).filter((id) => antes[id] && !sel[id]);
      await onSalvar(entrar, sair);
      onFechar();
    } finally { setSalvando(false); }
  }

  return (
    <SheetWrap onClose={onFechar} maxHeight="88%">
      <div style={{ padding: '10px 20px 0', flex: 'none' }}>
        <h3 style={{ fontFamily: 'var(--serif)', fontSize: 23, margin: '6px 0 4px', color: 'var(--cream)' }}>
          {(estante.emoji ? estante.emoji + ' ' : '') + estante.name}
        </h3>
        <p style={{ fontSize: 13.5, color: 'var(--muted)', margin: '0 0 14px' }}>
          {marcados === 0 ? 'Nenhum livro aqui ainda.' : `${marcados} ${marcados === 1 ? 'livro' : 'livros'} nesta estante.`}
        </p>
        <input
          value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar na sua biblioteca…"
          style={{ width: '100%', padding: '11px 14px', borderRadius: 12, border: '1px solid var(--hair)', background: 'var(--card-2)', color: 'var(--cream)', fontSize: 14.5, outline: 'none', fontFamily: 'inherit', marginBottom: 8 }}
        />
      </div>
      <div className="om-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '4px 20px 8px' }}>
        {visiveis.length === 0 && (
          <div style={{ padding: '30px 4px', textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>
            {busca ? 'Nenhum livro com esse nome.' : 'Sua biblioteca ainda está vazia.'}
          </div>
        )}
        {visiveis.map((b: any) => {
          const on = !!sel[b.id];
          return (
            <button key={b.id} onClick={() => setSel((v: any) => ({ ...v, [b.id]: !v[b.id] }))} style={{
              display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
              padding: '9px 4px', border: 'none', background: 'transparent', cursor: 'pointer',
            }}>
              <BookCover book={b} width={38} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14.5, color: 'var(--cream)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.title}</div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.author}</div>
              </div>
              <span style={{
                width: 23, height: 23, flex: 'none', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: on ? 'none' : '1.5px solid var(--hair)', background: on ? 'var(--amber)' : 'transparent',
              }}>
                {on && <Icon name="check" size={14} color="#1a120c" />}
              </span>
            </button>
          );
        })}
      </div>
      <div style={{ flex: 'none', padding: '10px 20px calc(18px + env(safe-area-inset-bottom,0px))', borderTop: '1px solid var(--hair)' }}>
        <button onClick={salvar} disabled={salvando} style={{
          width: '100%', padding: '15px 20px', borderRadius: 999, border: 'none',
          background: 'var(--amber)', color: '#1a120c', fontSize: 15, fontWeight: 700, cursor: 'pointer',
        }}>{salvando ? 'guardando…' : 'Pronto'}</button>
      </div>
    </SheetWrap>
  );
}

// Organizar: a ordem, o nome e a exclusão num lugar só. Arrastar aqui é fácil
// porque são linhas compactas — arrastar seções inteiras com grade de capas no
// meio seria um gesto brigando com a rolagem.
function OrganizarEstantesSheet({ estantes, contagem, onReordenar, onRenomear, onExcluir, onFechar }: any) {
  const [lista, setLista] = useState(() => [...(estantes || [])]);
  const [arrastando, setArrastando] = useState<number | null>(null);
  const [sobre, setSobre] = useState<number | null>(null);
  const refs = useRef<any[]>([]);

  useEffect(() => { setLista([...(estantes || [])]); }, [estantes]);

  function iniciar(i: number, e: any) {
    e.preventDefault();
    setArrastando(i); setSobre(i);
    const mover = (ev: any) => {
      const y = ev.touches ? ev.touches[0].clientY : ev.clientY;
      let alvo = i;
      refs.current.forEach((el, k) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (y > r.top && y < r.bottom) alvo = k;
      });
      setSobre(alvo);
    };
    const soltar = () => {
      window.removeEventListener('mousemove', mover); window.removeEventListener('mouseup', soltar);
      window.removeEventListener('touchmove', mover); window.removeEventListener('touchend', soltar);
      setArrastando((de) => {
        setSobre((para) => {
          if (de !== null && para !== null && de !== para) {
            setLista((l) => {
              const nova = [...l];
              const [x] = nova.splice(de, 1);
              nova.splice(para, 0, x);
              onReordenar(nova.map((s: any) => s.id));
              return nova;
            });
          }
          return null;
        });
        return null;
      });
    };
    window.addEventListener('mousemove', mover); window.addEventListener('mouseup', soltar);
    window.addEventListener('touchmove', mover, { passive: false }); window.addEventListener('touchend', soltar);
  }

  return (
    <SheetWrap onClose={onFechar} maxHeight="80%">
      <div style={{ padding: '10px 20px 0', flex: 'none' }}>
        <h3 style={{ fontFamily: 'var(--serif)', fontSize: 23, margin: '6px 0 4px', color: 'var(--cream)' }}>Organizar estantes</h3>
        <p style={{ fontSize: 13.5, color: 'var(--muted)', margin: '0 0 14px', lineHeight: 1.5 }}>
          Arraste para mudar a ordem. Excluir uma estante <b>não apaga livro nenhum</b> — eles continuam na sua biblioteca.
        </p>
      </div>
      <div className="om-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 14px 8px' }}>
        {lista.map((s: any, i: number) => (
          <div
            key={s.id}
            ref={(el) => { refs.current[i] = el; }}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '11px 8px', borderRadius: 13,
              background: arrastando === i ? 'var(--card-2)' : 'transparent',
              opacity: arrastando !== null && arrastando !== i && sobre === i ? 0.55 : 1,
              transition: 'background .15s',
            }}
          >
            <span
              onMouseDown={(e) => iniciar(i, e)} onTouchStart={(e) => iniciar(i, e)}
              style={{ flex: 'none', cursor: 'grab', color: 'var(--muted)', padding: '4px 2px', touchAction: 'none' }}
              aria-label="Arrastar"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round">
                <line x1="5" y1="9" x2="19" y2="9" /><line x1="5" y1="15" x2="19" y2="15" />
              </svg>
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, color: 'var(--cream)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {(s.emoji ? s.emoji + ' ' : '') + s.name}
              </div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                {(contagem[s.id] || 0)} {(contagem[s.id] || 0) === 1 ? 'livro' : 'livros'}
              </div>
            </div>
            <button onClick={() => onRenomear(s)} style={{ flex: 'none', border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', padding: 6 }} aria-label="Renomear">
              <Icon name="edit" size={16} />
            </button>
            <button onClick={() => onExcluir(s)} style={{ flex: 'none', border: 'none', background: 'transparent', color: 'var(--danger)', cursor: 'pointer', padding: 6, fontSize: 12.5 }}>
              Excluir
            </button>
          </div>
        ))}
      </div>
    </SheetWrap>
  );
}

const SHELF_META: any = { lendo: { label: 'Lendo', color: 'var(--amber)' }, lido: { label: 'Lidos', color: 'var(--green)' }, quero: { label: 'Quero ler', color: 'var(--lapis)' } };
function LibraryTab({ data, isMe, onAddBook, onFollow, onOpenBook, myBooks, myShelves, estantes, onAbrirEstudos }: any) {
  const [dim, setDim] = useState('status'); // hook sempre no topo (antes de qualquer return)
  const [sheet, setSheet] = useState<any>(null); // { tipo: 'nova'|'renomear'|'livros'|'organizar', estante? }
  if (data.locked) return <LockedCard name={data.name} onFollow={onFollow} />;
  const rawBooks = data.books || [];
  if (rawBooks.length === 0) {
    // ESTANTE VAZIA NÃO PODE TRANCAR OS CADERNOS.
    //
    // Este early return vinha ANTES do botão, então quem não tinha livro
    // ficava sem NENHUM caminho de interface até as próprias anotações — só
    // digitando /cadernos na barra de endereço. E agora existe nota livre,
    // que nem precisa de livro para existir: a porta tinha que sair da
    // dependência da estante.
    return (
      <div style={{ marginTop: 8 }}>
        {/* O botão "Abrir meus cadernos" que morava aqui saiu: ele existia
            porque a estante vazia trancava o caminho até as anotações, e a
            aba Cadernos na fileira de cima resolve isso em todo estado da
            tela — com livro ou sem nenhum. */}
        <EmptyState icon="shelf" title={isMe ? 'Sua estante está esperando o primeiro livro' : 'Estante vazia'} body={isMe ? 'Adicione um livro que está lendo ou quer ler.' : 'Ainda sem livros na estante.'} action={isMe ? 'Adicionar livro' : null} onAction={onAddBook} />
      </div>
    );
  }
  // Só a PRÓPRIA estante ganha gênero/estante (enriquece pelo acervo do App, por id).
  const canDim = isMe && Array.isArray(myBooks);
  let books = rawBooks;
  if (canDim) {
    const m: any = {}; myBooks.forEach((b: any) => { m[b.id] = { genre: b.genre || null, shelfIds: b.shelfIds || [] }; });
    books = rawBooks.map((b: any) => ({ ...b, genre: m[b.id]?.genre ?? null, shelfIds: m[b.id]?.shelfIds ?? [] }));
  }
  const shelves = (canDim && Array.isArray(myShelves)) ? myShelves : [];
  const useDim = canDim ? dim : 'status';
  // A edicao e do DONO. `myShelves` chega tambem no perfil dos outros (o App
  // passa nos dois casos), entao o gate tem que ser `isMe`, nunca a presenca
  // dos dados.
  const podeEditar = !!isMe && !!estantes;
  const contagem: any = {};
  shelves.forEach((sh: any) => { contagem[sh.id] = books.filter((b: any) => (b.shelfIds || []).includes(sh.id)).length; });

  let sections: any[] = [];
  if (useDim === 'status') {
    const groups: any = { lendo: [], lido: [], quero: [] };
    books.forEach((b: any) => groups[statusKey(b)].push(b));
    sections = ['lendo', 'lido', 'quero'].filter((k) => groups[k].length).map((k) => ({ key: k, label: SHELF_META[k].label, color: SHELF_META[k].color, books: groups[k] }));
  } else if (useDim === 'genre') {
    const byG: any = {};
    books.forEach((b: any) => { const g = b.genre || '__none'; (byG[g] = byG[g] || []).push(b); });
    const keys = Object.keys(byG).filter((k) => k !== '__none').sort((a, b) => a.localeCompare(b));
    if (byG['__none']) keys.push('__none');
    sections = keys.map((k) => ({ key: k, label: k === '__none' ? 'Sem gênero' : k, books: byG[k] }));
  } else { // shelf
    // Estante vazia continuava invisivel — o que tornava impossivel criar uma
    // aqui: voce criava e nada aparecia. Para o dono, ela aparece com o convite
    // de escolher livros; para visitante, some como antes.
    sections = shelves.map((s: any) => ({
      key: s.id, estante: s,
      label: (s.emoji ? s.emoji + ' ' : '') + s.name,
      books: books.filter((b: any) => (b.shelfIds || []).includes(s.id)),
    }));
    if (!podeEditar) sections = sections.filter((sec: any) => sec.books.length);
  }

  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{ fontSize: 13.5, color: 'var(--muted)', marginBottom: canDim ? 14 : 22 }}><strong style={{ color: 'var(--cream)', fontFamily: 'var(--serif)', fontSize: 17 }}>{rawBooks.length}</strong> livros na estante</div>
      {canDim && (
        <div style={{ display: 'inline-flex', gap: 4, background: 'var(--card-2)', borderRadius: 12, padding: 4, marginBottom: 22 }}>
          {[['status', 'Status'], ['genre', 'Gênero'], ['shelf', 'Estante']].map(([d, l]) => (
            <button key={d} onClick={() => setDim(d)} style={{ padding: '7px 14px', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, background: dim === d ? 'var(--amber)' : 'transparent', color: dim === d ? '#1a120c' : 'var(--muted)' }}>{l}</button>
          ))}
          {/* O chip "Cadernos" saiu daqui. Ele ficava ao lado das dimensões de
              ordenação — o último lugar onde alguém procuraria as próprias
              anotações —, e ainda por cima precisava de uma divisória e de uma
              seta para avisar que não era uma quarta dimensão. Quando um botão
              precisa se desculpar pelo lugar onde está, o lugar é que está
              errado. Agora é aba, ao lado de Biblioteca e Clubes. */}
        </div>
      )}
      {podeEditar && useDim === 'shelf' && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <button onClick={() => setSheet({ tipo: 'nova' })} style={{
            display: 'flex', alignItems: 'center', gap: 7, padding: '9px 15px', borderRadius: 999,
            border: '1px dashed var(--hair)', background: 'transparent', color: 'var(--cream)',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>
            <Icon name="plus" size={14} /> Nova estante
          </button>
          {shelves.length > 1 && (
            <button onClick={() => setSheet({ tipo: 'organizar' })} style={{
              padding: '9px 15px', borderRadius: 999, border: '1px solid var(--hair)',
              background: 'transparent', color: 'var(--muted)', fontSize: 13, cursor: 'pointer',
            }}>Organizar</button>
          )}
        </div>
      )}
      {sections.length === 0 ? (
        <div style={{ fontSize: 13.5, color: 'var(--muted)', padding: '14px 0', lineHeight: 1.5 }}>{useDim === 'shelf' ? (podeEditar ? 'Você ainda não criou nenhuma estante. Comece por “Nova estante” aqui em cima.' : 'Nenhuma estante com livros ainda.') : 'Nada por aqui.'}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
          {sections.map((sec: any) => (
            <div key={sec.key}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
                {sec.color && <span style={{ width: 9, height: 9, borderRadius: '50%', background: sec.color }} />}
                <h2 style={{ fontFamily: 'var(--serif)', fontSize: 21, margin: 0, color: 'var(--cream)' }}>{sec.label}</h2>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)' }}>{sec.books.length}</span>
                {podeEditar && sec.estante && (
                  <button onClick={() => setSheet({ tipo: 'livros', estante: sec.estante })} style={{
                    marginLeft: 'auto', border: '1px solid var(--hair)', background: 'transparent',
                    color: 'var(--muted)', borderRadius: 999, padding: '6px 12px', fontSize: 12, cursor: 'pointer',
                  }}>Escolher livros</button>
                )}
              </div>
              {sec.estante && sec.books.length === 0 && (
                <button onClick={() => setSheet({ tipo: 'livros', estante: sec.estante })} style={{
                  width: '100%', textAlign: 'left', padding: '16px 16px', borderRadius: 14,
                  border: '1px dashed var(--hair)', background: 'transparent', color: 'var(--muted)',
                  fontSize: 13.5, lineHeight: 1.5, cursor: 'pointer',
                }}>
                  Estante vazia — toque para escolher os livros que moram aqui.
                </button>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, columnGap: 14 }}>
                {sec.books.map((b: any) => {
                  const pct = b.status === 'lendo' && b.totalPages ? Math.min(100, Math.round((b.currentPage / b.totalPages) * 100)) : 0;
                  return (
                    <div key={b.id} onClick={() => onOpenBook && b.id && onOpenBook(b.id)} style={{ cursor: onOpenBook ? 'pointer' : 'default' }}>
                      <BookCover book={b} width={104} />
                      {b.status === 'lendo' && <div style={{ marginTop: 8 }}><div style={{ height: 4, borderRadius: 3, background: 'var(--card-2)', overflow: 'hidden' }}><div style={{ width: `${pct}%`, height: '100%', background: 'var(--amber)' }} /></div><div style={{ fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--amber)', marginTop: 4 }}>{pct}%</div></div>}
                      {b.status === 'lido' && b.finishedAt && <div style={{ fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--muted)', marginTop: 7 }}>{fmtDate(b.finishedAt)}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {sheet?.tipo === 'nova' && (
        <EstanteSheet onFechar={() => setSheet(null)} onSalvar={(patch: any) => estantes.criar(patch)} />
      )}
      {sheet?.tipo === 'renomear' && (
        <EstanteSheet inicial={sheet.estante} onFechar={() => setSheet(null)}
          onSalvar={(patch: any) => estantes.renomear(sheet.estante.id, patch)} />
      )}
      {sheet?.tipo === 'livros' && (
        <LivrosDaEstanteSheet
          estante={sheet.estante}
          livros={myBooks || []}
          onFechar={() => setSheet(null)}
          onSalvar={(entrar: string[], sair: string[]) => estantes.vincular(sheet.estante.id, entrar, sair)}
        />
      )}
      {sheet?.tipo === 'organizar' && (
        <OrganizarEstantesSheet
          estantes={shelves}
          contagem={contagem}
          onReordenar={(ids: string[]) => estantes.reordenar(ids)}
          onRenomear={(sh: any) => setSheet({ tipo: 'renomear', estante: sh })}
          onExcluir={async (sh: any) => {
            // Sem `window.confirm`: a frase ja diz que nenhum livro se perde,
            // e a estante volta a existir com dois toques.
            await estantes.excluir(sh.id);
          }}
          onFechar={() => setSheet(null)}
        />
      )}
    </div>
  );
}
function ClubsTab({ data, isMe, onOpenClub, onExploreClubs, onFollow }: any) {
  if (data.locked) return <LockedCard name={data.name} onFollow={onFollow} />;
  const clubs = data.clubs || [];
  if (clubs.length === 0) return <div style={{ marginTop: 8 }}><EmptyState icon="users" title="Ainda sem clubes" body={isMe ? 'Entre num clube de leitura pra ler em boa companhia.' : 'Não participa de clubes públicos.'} action={isMe ? 'Explorar clubes' : null} onAction={onExploreClubs} /></div>;
  const lead = clubs.filter((c: any) => c.role === 'owner' || c.role === 'admin').length;
  return (
    <div style={{ padding: '8px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: 13.5, color: 'var(--muted)', marginBottom: 2 }}><strong style={{ color: 'var(--cream)', fontFamily: 'var(--serif)', fontSize: 17 }}>{clubs.length}</strong> {clubs.length === 1 ? 'clube' : 'clubes'}{lead > 0 ? ` · líder de ${lead}` : ''}</div>
      {clubs.map((c: any) => <ClubCard key={c.id} club={c} wide onClick={() => onOpenClub && onOpenClub(c.id)} />)}
    </div>
  );
}
function AchievementsTab({ data, onFollow }: any) {
  if (data.locked) return <LockedCard name={data.name} onFollow={onFollow} />;
  const achs = data.achievements || [];
  const unlocked = achs.filter((a: any) => a.unlocked).length;
  if (achs.length === 0) return <div style={{ marginTop: 8 }}><EmptyState icon="trophy" title="Sem conquistas ainda" body="Continue lendo pra desbloquear conquistas." /></div>;
  return (
    <div style={{ padding: '8px 0' }}>
      <Card style={{ padding: 16, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 14 }} glow>
        <div style={{ width: 50, height: 50, borderRadius: 14, background: 'rgba(232,122,59,.16)', color: 'var(--amber)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="trophy" size={26} /></div>
        <div>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 24, color: 'var(--cream)', lineHeight: 1 }}>{unlocked} de {achs.length}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>conquistas desbloqueadas</div>
        </div>
      </Card>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 26, rowGap: 28 }}>
        {achs.map((a: any, i: number) => <AchievementBadge key={a.id} a={a} idx={i} />)}
      </div>
    </div>
  );
}

// ── Sheets ──────────────────────────────────────────────────────────────────
function SheetWrap({ onClose, children, maxHeight }: any) {
  return (
    <div onClick={onClose} className="pfs-fade" style={{ position: 'fixed', inset: 0, zIndex: 9600, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: 'rgba(0,0,0,.55)', backdropFilter: 'blur(2px)' }}>
      <div onClick={(e) => e.stopPropagation()} className="pfs-up" style={{ width: '100%', maxWidth: 460, maxHeight: maxHeight || '82%', background: 'var(--card)', borderRadius: '22px 22px 0 0', borderTop: '1px solid var(--hair)', display: 'flex', flexDirection: 'column', boxShadow: '0 -10px 40px rgba(0,0,0,.5)' }}>
        <div style={{ padding: '12px 0 0', display: 'flex', justifyContent: 'center' }}><div style={{ width: 38, height: 4, borderRadius: 3, background: 'var(--hair)' }} /></div>
        {children}
      </div>
    </div>
  );
}
function PersonRow({ person, onToggle, busy, onOpen }: any) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0' }}>
      <button onClick={() => onOpen && onOpen(person.id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0, textAlign: 'left' }}>
        <Avatar name={person.name} size={46} url={person.avatarUrl} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--cream)' }}>{person.name}</div>
          {person.username && <div style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--muted)' }}>@{person.username}</div>}
          {person.bio && <div style={{ fontSize: 12.5, color: 'var(--cream-2)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}><MentionText text={person.bio} /></div>}
        </div>
      </button>
      {!person.isMe && (person.isFollowing
        ? <BtnGhost onClick={onToggle} style={{ padding: '6px 14px', fontSize: 12.5, opacity: busy ? 0.6 : 1 }}>Seguindo</BtnGhost>
        : person.requested
          ? <BtnGhost onClick={onToggle} style={{ padding: '6px 12px', fontSize: 12.5, opacity: busy ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="clock" size={12} /> Solicitado</BtnGhost>
          : <BtnAmber onClick={onToggle} disabled={busy} style={{ padding: '6px 14px', fontSize: 12.5 }}>Seguir</BtnAmber>)}
    </div>
  );
}

// ─── Atualização "tempo real" leve ───────────────────────────────────────────
// Re-busca ao voltar o foco (visibilitychange→visible) + poll enquanto a tela
// está visível. Pausa em background pra não martelar o Supabase (o gargalo).
// `refetch` é lido sempre da última versão via ref, então mudar a closure não
// reinicia o intervalo.
function useLiveRefresh(refetch: () => void, intervalMs: number, enabled: boolean = true) {
  const ref = useRef(refetch);
  ref.current = refetch;
  useEffect(() => {
    if (!enabled) return;
    let timer: any = null;
    // jitter: dessincroniza ticks entre clientes (evita rajadas no Supabase quando
    // muita gente volta pro app ao mesmo tempo, ex.: push em massa).
    const start = () => { if (timer == null && intervalMs > 0) timer = setInterval(() => ref.current(), intervalMs + Math.floor(Math.random() * 4000)); };
    const stop = () => { if (timer != null) { clearInterval(timer); timer = null; } };
    const onVis = () => {
      if (document.visibilityState === 'visible') { ref.current(); start(); }
      else stop();
    };
    document.addEventListener('visibilitychange', onVis);
    if (document.visibilityState === 'visible') start();
    return () => { document.removeEventListener('visibilitychange', onVis); stop(); };
  }, [intervalMs, enabled]);
}

function PeopleSheet({ session, profile, tab, onClose, onOpenProfile, onFollowDelta, onMutate }: any) {
  const [active, setActive] = useState(tab || 'followers');
  const [people, setPeople] = useState<any>(null);
  const [busy, setBusy] = useState<any>({});
  const busyRef = useRef<any>({}); busyRef.current = busy;
  const mutRef = useRef(0); // até quando NÃO confiar no poll (cooldown pós-toggle)
  useEffect(() => { setActive(tab || 'followers'); }, [tab]);
  useEffect(() => {
    let alive = true; setPeople(null);
    const fn = active === 'followers' ? api.listFollowers : api.listFollowing;
    fn(session, profile.id).then((r: any) => { if (alive) setPeople(r.people || []); }).catch(() => { if (alive) setPeople([]); });
    return () => { alive = false; };
  }, [active, profile.id]);
  // Tempo real: atualiza a lista sem piscar. Pula se há ação em voo OU dentro do
  // cooldown pós-toggle — evita o poll reverter o otimista com leitura stale do
  // Supabase (read-after-write) e cobre a janela do setBusy ainda não comitado.
  const softFetch = useCallback(() => {
    if (Object.values(busyRef.current).some(Boolean) || Date.now() < mutRef.current) return;
    const fn = active === 'followers' ? api.listFollowers : api.listFollowing;
    fn(session, profile.id).then((r: any) => setPeople(r.people || [])).catch(() => {});
  }, [active, profile.id, session]);
  useLiveRefresh(softFetch, 20000);
  const toggle = async (p: any) => {
    if (busy[p.id]) return;
    mutRef.current = Date.now() + 12000; // trava o poll ANTES do await (síncrono)
    onMutate && onMutate(); // arma o cooldown do perfil pai também
    setBusy((b: any) => ({ ...b, [p.id]: true }));
    // Ativo = já segue OU já solicitou (privado) → o toque DESFAZ. Senão, segue/solicita.
    const doFollow = !(p.isFollowing || p.requested);
    try {
      // Usa o RETORNO real: perfil privado vira "Solicitado" (requested), não "Seguindo".
      const r: any = await (doFollow ? api.followUser : api.unfollowUser)(session, p.id);
      setPeople((ppl: any) => ppl.map((x: any) => x.id === p.id ? { ...x, isFollowing: !!r?.following, requested: !!r?.requested } : x));
      const delta = (r?.following ? 1 : 0) - (p.isFollowing ? 1 : 0);
      if (onFollowDelta && delta) onFollowDelta(delta);
    } catch { /* mantém o estado atual */ }
    finally { mutRef.current = Date.now() + 12000; setBusy((b: any) => ({ ...b, [p.id]: false })); }
  };
  return (
    <SheetWrap onClose={onClose}>
      <div style={{ display: 'flex', padding: '14px 18px 0', gap: 4 }}>
        {[['following', `Seguindo · ${active === 'following' && people ? people.length : profile.following}`], ['followers', `Seguidores · ${active === 'followers' && people ? people.length : profile.followers}`]].map(([id, lbl]: any) => (
          <button key={id} onClick={() => setActive(id)} style={{ flex: 1, padding: '10px', borderRadius: 11, border: 'none', cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 600, background: active === id ? 'var(--card-2)' : 'transparent', color: active === id ? 'var(--cream)' : 'var(--muted)' }}>{lbl}</button>
        ))}
      </div>
      <div style={{ overflowY: 'auto', padding: '4px 18px 28px' }}>
        {people === null && <div style={{ padding: '30px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>Carregando…</div>}
        {people && people.length === 0 && <div style={{ padding: '30px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 13.5 }}>{active === 'followers' ? 'Ninguém ainda.' : 'Não segue ninguém ainda.'}</div>}
        {people && people.map((p: any) => <PersonRow key={p.id} person={p} busy={busy[p.id]} onToggle={() => toggle(p)} onOpen={(id: string) => { onClose(); onOpenProfile && onOpenProfile(id); }} />)}
      </div>
    </SheetWrap>
  );
}
function RequestsSheet({ session, onClose, onResolved, onOpenProfile, onMutate }: any) {
  const [list, setList] = useState<any>(null);
  const [busy, setBusy] = useState<any>({});
  const busyRef = useRef<any>({}); busyRef.current = busy;
  const mutRef = useRef(0); // cooldown pós-aprovar/recusar (não repor linha por leitura stale)
  useEffect(() => { let a = true; api.listFollowRequests(session).then((r: any) => { if (a) setList(r.requests || []); }).catch(() => { if (a) setList([]); }); return () => { a = false; }; }, []);
  // Tempo real: novas solicitações aparecem sem reabrir a sheet. Pula com ação em
  // voo OU dentro do cooldown — evita repor uma solicitação recém-resolvida por
  // leitura stale do Supabase e cobre a janela do setBusy ainda não comitado.
  const softFetch = useCallback(() => {
    if (Object.values(busyRef.current).some(Boolean) || Date.now() < mutRef.current) return;
    api.listFollowRequests(session).then((r: any) => setList(r.requests || [])).catch(() => {});
  }, [session]);
  useLiveRefresh(softFetch, 15000);
  const act = async (pp: any, kind: string) => {
    if (busy[pp.id]) return;
    mutRef.current = Date.now() + 12000; // trava o poll ANTES do await (síncrono)
    onMutate && onMutate(); // arma o cooldown do perfil pai (evita flicker no contador do dono)
    setBusy((b: any) => ({ ...b, [pp.id]: true }));
    setList((l: any) => l.filter((x: any) => x.id !== pp.id));
    try {
      await (kind === 'ok' ? api.approveFollowRequest : api.rejectFollowRequest)(session, pp.id);
      if (onResolved) onResolved(kind === 'ok');
    } catch (e: any) {
      setList((l: any) => [pp, ...(l || [])]);
      window.alert(e?.message || 'Erro');
    } finally { mutRef.current = Date.now() + 12000; setBusy((b: any) => ({ ...b, [pp.id]: false })); }
  };
  return (
    <SheetWrap onClose={onClose}>
      <div style={{ padding: '14px 18px 6px' }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4 }}>Perfil privado</div>
        <h2 style={{ fontFamily: 'var(--serif)', fontSize: 23, margin: 0, color: 'var(--cream)' }}>Solicitações</h2>
      </div>
      <div style={{ overflowY: 'auto', padding: '4px 18px 28px' }}>
        {list === null && <div style={{ padding: '30px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>Carregando…</div>}
        {list && list.length === 0 && <div style={{ padding: '30px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 13.5 }}>Nenhuma solicitação no momento.</div>}
        {list && list.map((pp: any) => (
          <div key={pp.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0' }}>
            <button onClick={() => { onClose(); onOpenProfile && onOpenProfile(pp.id); }} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0, textAlign: 'left' }}>
              <Avatar name={pp.name} size={46} url={pp.avatarUrl} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--cream)' }}>{pp.name}</div>
                {pp.username && <div style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--muted)' }}>@{pp.username}</div>}
                <div style={{ fontSize: 12, color: 'var(--cream-2)', marginTop: 2 }}>{relTime(pp.when)}</div>
              </div>
            </button>
            <div style={{ display: 'flex', gap: 7 }}>
              <BtnAmber onClick={() => act(pp, 'ok')} style={{ padding: '7px 14px', fontSize: 12.5 }}>Aceitar</BtnAmber>
              <BtnGhost onClick={() => act(pp, 'no')} style={{ padding: '7px 12px', fontSize: 12.5 }}>Recusar</BtnGhost>
            </div>
          </div>
        ))}
      </div>
    </SheetWrap>
  );
}

function FriendsStrip({ items, onOpenProfile }: any) {
  return (
    <div className="pfs-noscroll" style={{ display: 'flex', gap: 10, overflowX: 'auto', padding: '2px 18px 4px', margin: '0 -18px', scrollbarWidth: 'none' }}>
      {items.map((f: any, i: number) => {
        const tail = f.type === 'finished'
          ? <><span style={{ color: 'var(--muted)' }}>terminou </span><span className="pfs-em">{f.book}</span></>
          : <><span style={{ color: 'var(--muted)' }}>leu {f.pages} págs de </span><span className="pfs-em">{f.book}</span></>;
        return (
          <button key={i} onClick={() => f.who && onOpenProfile && onOpenProfile(f.who.id)} style={{ flexShrink: 0, width: 186, background: 'var(--card-2)', border: '1px solid var(--hair)', borderRadius: 13, padding: 12, textAlign: 'left', cursor: 'pointer' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
              <Avatar name={f.who && f.who.name} size={28} url={f.who && f.who.avatarUrl} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, color: 'var(--cream)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{((f.who && f.who.name) || '').split(' ')[0]}</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--muted)' }}>{relTime(f.when)}</div>
              </div>
            </div>
            <div style={{ fontSize: 12.5, lineHeight: 1.4 }}>{tail}</div>
          </button>
        );
      })}
    </div>
  );
}

function ShareSheet({ profile, onClose }: any) {
  const [copied, setCopied] = useState(false);
  const url = `forjamente.com.br/@${profile.username || profile.id}`;
  const copy = () => { try { navigator.clipboard && navigator.clipboard.writeText('https://' + url); } catch (e) {} setCopied(true); setTimeout(() => setCopied(false), 1800); };
  return (
    <SheetWrap onClose={onClose} maxHeight="auto">
      <div style={{ padding: '16px 20px 40px' }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>Compartilhar perfil</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 14, background: 'var(--card-2)', border: '1px solid var(--hair)', marginBottom: 16 }}>
          <Avatar name={profile.name} size={44} url={profile.avatarUrl} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 17, color: 'var(--cream)', lineHeight: 1.1 }}>{profile.name}</div>
            {profile.username && <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)' }}>@{profile.username}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 6px 5px 14px', borderRadius: 12, background: 'var(--card-2)', border: '1px solid var(--hair)' }}>
          <span style={{ flex: 1, minWidth: 0, fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--cream-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{url}</span>
          {copied ? <BtnGhost onClick={copy} style={{ padding: '9px 16px' }}><Icon name="check" size={15} /> Copiado</BtnGhost> : <BtnAmber onClick={copy} style={{ padding: '9px 16px' }}>Copiar</BtnAmber>}
        </div>
      </div>
    </SheetWrap>
  );
}
const REPORT_REASONS = ['Spam ou golpe', 'Conteúdo impróprio', 'Assédio ou bullying', 'Perfil falso', 'Outro motivo'];
function MoreSheet({ session, profile, onClose, onBlocked }: any) {
  const [view, setView] = useState('menu'); const [reason, setReason] = useState<any>(null);
  const [done, setDone] = useState<any>(null); const [busy, setBusy] = useState(false);
  const first = (profile.name || '').split(' ')[0];
  const act = async (kind: string) => {
    if (busy) return; setBusy(true);
    try {
      if (kind === 'block') { await api.blockUser(session, profile.id); setDone('block'); setTimeout(() => { onClose(); onBlocked && onBlocked(); }, 1300); }
      else if (kind === 'unblock') { await api.unblockUser(session, profile.id); onClose(); onBlocked && onBlocked(); }
      else { await api.reportUser(session, profile.id, reason || ''); setDone('report'); setTimeout(onClose, 1300); }
    } catch (e: any) { window.alert(e?.message || 'Erro'); setBusy(false); }
  };
  if (view === 'block' || view === 'report') {
    const isBlock = view === 'block';
    return (
      <div onClick={onClose} className="pfs-fade" style={{ position: 'fixed', inset: 0, zIndex: 9620, background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22 }}>
        <div onClick={(e) => e.stopPropagation()} className="pfs-pop" style={{ width: '100%', maxWidth: 360, background: 'var(--card)', borderRadius: 20, border: '1px solid var(--hair)', padding: 22, boxShadow: '0 20px 60px rgba(0,0,0,.6)' }}>
          {done ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, margin: '0 auto 14px', background: 'rgba(158,176,131,.16)', color: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="check" size={26} /></div>
              <h3 style={{ fontFamily: 'var(--serif)', fontSize: 22, margin: '0 0 6px', color: 'var(--cream)' }}>{done === 'block' ? 'Usuário bloqueado' : 'Denúncia enviada'}</h3>
              <p style={{ fontSize: 13.5, color: 'var(--muted)', margin: 0, lineHeight: 1.5 }}>{done === 'block' ? `Você não verá mais @${profile.username || first}.` : 'Obrigado. Nossa equipe vai revisar.'}</p>
            </div>
          ) : (
            <>
              <div style={{ width: 48, height: 48, borderRadius: 13, background: 'rgba(192,58,42,.14)', color: 'var(--danger)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}><Icon name={isBlock ? 'block' : 'flag'} size={24} /></div>
              <h3 style={{ fontFamily: 'var(--serif)', fontSize: 23, margin: '0 0 8px', color: 'var(--cream)' }}>{isBlock ? `Bloquear ${first}?` : 'Denunciar usuário'}</h3>
              <p style={{ fontSize: 13.5, color: 'var(--muted)', margin: '0 0 16px', lineHeight: 1.5 }}>{isBlock ? `Vocês deixam de se ver e de se seguir. ${first} não será avisado e você pode desbloquear quando quiser.` : 'Escolha o motivo. Sua denúncia é anônima e será revisada.'}</p>
              {!isBlock && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
                  {REPORT_REASONS.map((r) => { const on = reason === r; return (
                    <button key={r} onClick={() => setReason(r)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', borderRadius: 11, cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 13.5, textAlign: 'left', background: on ? 'rgba(232,122,59,.12)' : 'var(--card-2)', border: `1px solid ${on ? 'var(--amber)' : 'var(--hair)'}`, color: on ? 'var(--cream)' : 'var(--cream-2)' }}>{r}{on && <Icon name="check" size={16} color="var(--amber)" />}</button>
                  ); })}
                </div>
              )}
              <div style={{ display: 'flex', gap: 9 }}>
                <BtnGhost onClick={() => setView('menu')} style={{ flex: 1 }}>Voltar</BtnGhost>
                <button onClick={() => (isBlock || reason) && act(isBlock ? 'block' : 'report')} disabled={(!isBlock && !reason) || busy} style={{ flex: 1, justifyContent: 'center', display: 'flex', alignItems: 'center', fontFamily: 'var(--sans)', fontWeight: 600, fontSize: 14, padding: '11px', borderRadius: 11, border: 'none', background: (isBlock || reason) ? 'var(--danger)' : 'var(--card-2)', color: (isBlock || reason) ? '#fff' : 'var(--muted)', cursor: (isBlock || reason) ? 'pointer' : 'not-allowed', opacity: busy ? 0.6 : 1 }}>{isBlock ? 'Bloquear' : 'Denunciar'}</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
  return (
    <SheetWrap onClose={onClose} maxHeight="auto">
      <div style={{ padding: '14px 18px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <Avatar name={profile.name} size={38} url={profile.avatarUrl} />
          <div style={{ fontFamily: 'var(--mono)', fontSize: 12.5, color: 'var(--muted)' }}>@{profile.username || ''}</div>
        </div>
      </div>
      <div style={{ padding: '0 12px 28px' }}>
        {[
          // UX: se já bloqueado, oferece DESBLOQUEAR (antes não havia saída). Usa o iBlocked que já vem do backend.
          profile.iBlocked ? { ic: 'block', label: 'Desbloquear', go: 'unblock' } : { ic: 'block', label: 'Bloquear', go: 'block' },
          { ic: 'flag', label: 'Denunciar usuário', go: 'report' },
        ].map((o) => {
          const positive = o.go === 'unblock';
          return (
          <button key={o.go} onClick={() => positive ? act('unblock') : setView(o.go)} disabled={busy && positive} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 13, padding: '14px 12px', borderRadius: 12, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--sans)', fontSize: 15, fontWeight: 500, color: positive ? 'var(--ember)' : 'var(--danger)' }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: positive ? 'rgba(199,83,26,.12)' : 'rgba(192,58,42,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={o.ic} size={18} /></div>{o.label}
          </button>
          );
        })}
        <button onClick={onClose} style={{ width: '100%', marginTop: 6, padding: '13px', borderRadius: 12, background: 'var(--card-2)', border: '1px solid var(--hair)', cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 14.5, fontWeight: 600, color: 'var(--cream)' }}>Cancelar</button>
      </div>
    </SheetWrap>
  );
}
function EditProfileSheet({ session, data, onClose, onSaved }: any) {
  const [name, setName] = useState(data.name || '');
  const [bio, setBio] = useState(data.bio || '');
  const [priv, setPriv] = useState(!!data.isPrivate);
  const [username, setUsername] = useState((data.username || '').toLowerCase());
  const [avatarUrl, setAvatarUrl] = useState(data.avatarUrl || null);
  const [avatarPicked, setAvatarPicked] = useState<any>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<any>(null);
  const avatarInputRef = useRef<any>(null);

  const changedAt = data.usernameChangedAt ? new Date(data.usernameChangedAt).getTime() : 0;
  const cooldownMs = changedAt ? Math.max(0, 7 * 86400000 - (Date.now() - changedAt)) : 0;
  const unameLocked = cooldownMs > 0;
  const unameDaysLeft = Math.ceil(cooldownMs / 86400000);

  const onAvatarFile = (e: any) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) setAvatarPicked(f); };
  const confirmAvatar = async (cf: any) => {
    setAvatarPicked(null); setUploadingAvatar(true); setErr(null);
    try { const r = await api.uploadAvatar(session, cf); setAvatarUrl(r.url); onSaved && onSaved({ avatarUrl: r.url }); }
    catch (e: any) { setErr(e?.message || 'Erro ao enviar foto'); }
    finally { setUploadingAvatar(false); }
  };

  const save = async () => {
    if (busy) return; setBusy(true); setErr(null);
    const patch: any = { display_name: name.trim() || undefined, bio: bio, is_private: priv };
    const newU = username.trim().toLowerCase();
    if (!unameLocked && newU && newU !== (data.username || '').toLowerCase()) patch.username = newU;
    try {
      const up = await api.patchProfile(session, patch);
      onSaved && onSaved({ name: name.trim() || data.name, bio: bio.trim() || null, isPrivate: priv, username: (up && up.username) || data.username, usernameChangedAt: (up && up.username_changed_at) || data.usernameChangedAt });
      onClose();
    } catch (e: any) { setErr(e?.message || 'Erro ao salvar'); setBusy(false); }
  };

  return (
    <>
    <SheetWrap onClose={onClose} maxHeight="94%">
      <div style={{ overflowY: 'auto', padding: '16px 20px calc(28px + env(safe-area-inset-bottom,0px))' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: 24, margin: 0, color: 'var(--cream)' }}>Editar perfil</h2>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--card-2)', border: '1px solid var(--hair)', color: 'var(--cream)', display: 'grid', placeItems: 'center', cursor: 'pointer' }}><Icon name="close" size={16} /></button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
          <button onClick={() => avatarInputRef.current && avatarInputRef.current.click()} disabled={uploadingAvatar} style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            <Avatar name={name || data.name} size={92} url={avatarUrl} />
            <div style={{ position: 'absolute', right: -2, bottom: -2, width: 32, height: 32, borderRadius: '50%', background: 'var(--amber)', color: '#231007', display: 'grid', placeItems: 'center', border: '2px solid var(--card)' }}><Icon name="camera" size={16} /></div>
            {uploadingAvatar && <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(0,0,0,.55)', display: 'grid', placeItems: 'center', color: '#fff', fontSize: 12 }}>enviando…</div>}
            <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onAvatarFile} />
          </button>
        </div>

        <label style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--muted)' }}>Nome</label>
        <input value={name} onChange={(e) => setName(e.target.value.slice(0, 60))} placeholder="Seu nome" style={{ width: '100%', marginTop: 7, marginBottom: 18, padding: '12px 14px', borderRadius: 12, background: 'var(--card-2)', border: '1px solid var(--hair)', color: 'var(--cream)', fontSize: 15, fontFamily: 'var(--sans)' }} />

        <label style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--muted)' }}>Nome de usuário</label>
        <div style={{ position: 'relative', marginTop: 7 }}>
          <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', fontFamily: 'var(--mono)', fontSize: 15 }}>@</span>
          <input value={username} disabled={unameLocked} onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, '').slice(0, 30))} placeholder="usuario" style={{ width: '100%', padding: '12px 14px 12px 28px', borderRadius: 12, background: 'var(--card-2)', border: '1px solid var(--hair)', color: 'var(--cream)', fontSize: 15, fontFamily: 'var(--mono)', opacity: unameLocked ? 0.6 : 1 }} />
        </div>
        <div style={{ fontSize: 11.5, color: unameLocked ? 'var(--amber)' : 'var(--muted)', marginTop: 6, marginBottom: 18, lineHeight: 1.4 }}>{unameLocked ? `Você poderá mudar de novo em ${unameDaysLeft} dia(s).` : 'Único no app. Só pode ser alterado 1× a cada 7 dias.'}</div>

        <label style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--muted)' }}>Bio</label>
        <MentionInput multiline value={bio} onChange={(v) => setBio(v.slice(0, 200))} maxLength={200} placeholder="Conte do que você gosta de ler…" rows={3} dropdownPosition="below" inputStyle={{ width: '100%', marginTop: 7, padding: '12px 14px', borderRadius: 12, background: 'var(--card-2)', border: '1px solid var(--hair)', color: 'var(--cream)', fontSize: 15, fontFamily: 'var(--sans)', resize: 'none', lineHeight: 1.45 }} />
        <div style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--muted)', marginTop: 4 }}>{200 - bio.length}</div>

        <div onClick={() => setPriv((v: boolean) => !v)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 14px', borderRadius: 12, background: 'var(--card-2)', border: '1px solid var(--hair)', marginTop: 14, cursor: 'pointer' }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--card)', display: 'grid', placeItems: 'center', color: 'var(--amber)', flexShrink: 0 }}><Icon name="lock" size={17} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--cream)' }}>Perfil privado</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Quem quiser te seguir manda solicitação</div>
          </div>
          <div style={{ width: 44, height: 26, borderRadius: 999, background: priv ? 'var(--amber)' : 'var(--card)', border: '1px solid var(--hair)', position: 'relative', transition: 'background .18s', flexShrink: 0 }}>
            <div style={{ position: 'absolute', top: 2, left: priv ? 20 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .18s', boxShadow: '0 1px 3px rgba(0,0,0,.4)' }} />
          </div>
        </div>

        {err && <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 10, background: 'rgba(192,58,42,.1)', color: '#C03A2A', fontSize: 13, textAlign: 'center', lineHeight: 1.4 }}>{err}</div>}
        <BtnAmber onClick={save} disabled={busy} style={{ width: '100%', marginTop: 18, padding: '14px' }}>{busy ? 'Salvando…' : 'Salvar alterações'}</BtnAmber>
      </div>
    </SheetWrap>
    {avatarPicked && <CoverCropper file={avatarPicked} aspect={1} outW={480} outH={480} title="Ajustar foto" hint="Arraste pra posicionar · sua foto de perfil" onCancel={() => setAvatarPicked(null)} onConfirm={confirmAvatar} />}
    </>
  );
}

// ── Loading / Error ─────────────────────────────────────────────────────────
function Sk({ w, h, r = 8, style }: any) { return <div className="pfs-shim" style={{ width: w, height: h, borderRadius: r, background: 'var(--paper-3)', ...style }} />; }
function LoadingView() {
  return (
    <div style={{ padding: '20px 18px' }}>
      <div style={{ display: 'flex', gap: 16 }}>
        <Sk w={94} h={94} r={47} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 9 }}>
          <Sk w={150} h={22} /><Sk w={90} h={12} />
          <div style={{ display: 'flex', gap: 18, marginTop: 4 }}>{[0, 1, 2].map((i) => <Sk key={i} w={42} h={34} />)}</div>
        </div>
      </div>
      <Sk w="92%" h={13} style={{ marginTop: 16 }} />
      <div style={{ display: 'flex', gap: 7, marginTop: 14 }}>{[0, 1, 2].map((i) => <Sk key={i} w={84} h={30} r={999} />)}</div>
      <div style={{ display: 'flex', gap: 13, marginTop: 30, overflow: 'hidden' }}>{[0, 1, 2, 3].map((i) => <Sk key={i} w={116} h={174} r={5} style={{ flexShrink: 0 }} />)}</div>
      <Sk w="100%" h={130} r={16} style={{ marginTop: 24 }} />
    </div>
  );
}

// ── Meu Círculo (só o Mentor, no próprio perfil) ────────────────────────────
// O destaque do topo da aba Eu: o cartão abre o editor; o atalho, a página.
function MeuCirculo({ meu, onAbrir, onVerPagina }: any) {
  return (
    <div data-eu-meu-circulo="" style={{ margin: '16px 18px 0', padding: 6, borderRadius: 16, border: '1px solid var(--amber)', background: 'var(--card)', display: 'flex', alignItems: 'center', gap: 6 }}>
      <button onClick={onAbrir} style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 11, padding: '7px 8px', border: 'none', background: 'none', color: 'var(--cream)', textAlign: 'left', cursor: 'pointer' }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, flexShrink: 0, background: 'rgba(232,122,59,.14)', color: 'var(--amber)', display: 'grid', placeItems: 'center' }}><Icon name="users" size={18} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 18, lineHeight: 1.1 }}>Meu Círculo</div>
          {meu.serie?.titulo && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{meu.serie.titulo}</div>}
        </div>
        <Icon name="chevron" size={16} color="var(--muted)" />
      </button>
      <button data-eu-ver-pagina="" onClick={onVerPagina} style={{ flexShrink: 0, padding: '9px 12px', borderRadius: 11, border: '1px solid var(--hair)', background: 'var(--card-2)', color: 'var(--cream)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>Ver minha página</button>
    </div>
  );
}

// ── Componente principal ────────────────────────────────────────────────────
const ProfileSocialInner: React.FC<any> = ({
  session, userId, currentUserId, onBack, onOpenSettings, onOpenClub, onOpenProfile,
  onUpgrade, onAddBook, onExploreClubs, onProfileUpdated, onGoToEstudo, onOpenBook,
  myBooks, myShelves, estantes, onAbrirEstudos,
  souMentor = false, onAbrirMeuCirculo, onVerMinhaPagina,
}) => {
  const target = userId || currentUserId;
  const [data, setData] = useState<any>(null);
  const [err, setErr] = useState<any>(null);
  const [tab, setTab] = useState('visao');
  const [people, setPeople] = useState<any>(null);
  const [edit, setEdit] = useState(false);
  const [share, setShare] = useState(false);
  const [more, setMore] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [requests, setRequests] = useState(false);
  const [friends, setFriends] = useState<any>(null);
  const scrollRef = useRef<any>(null);
  const reqIdRef = useRef(0);
  const followLockRef = useRef(0); // até quando NÃO confiar no poll (cooldown pós-follow)
  const dataRef = useRef<any>(null); // espelho do data atual (decisões síncronas no poll)

  useEffect(() => { injectCss(); }, []);
  const load = useCallback(() => {
    // Sem id ainda (o perfil de quem está logado chega depois): esperar. Antes
    // saía GET /api/users/undefined → 404 → "Erro ao carregar perfil" (DEV-02).
    if (!target) return;
    const myId = ++reqIdRef.current; setData(null); setErr(null);
    api.getUserProfile(session, target)
      .then((d: any) => { if (reqIdRef.current === myId) setData(d); })
      .catch((e: any) => { if (reqIdRef.current === myId) setErr(e?.message || 'Erro ao carregar perfil'); });
  }, [session, target]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { dataRef.current = data; }, [data]);
  // Tempo real: atualiza SÓ os campos vivos (contadores + flags de relação) via
  // endpoint LEVE (sem re-buscar biblioteca/clubes/conquistas → poupa o Supabase,
  // o gargalo), sem piscar a tela nem re-renderizar abas pesadas. Guardado por
  // followBusy + cooldown pós-ação (followLockRef) pra o poll não reverter o
  // estado otimista com leitura stale (read-after-write).
  const softRefresh = useCallback(() => {
    if (!target || followBusy || Date.now() < followLockRef.current) return;
    const myId = ++reqIdRef.current;
    api.userSocialStatus(session, target)
      .then((d: any) => {
        if (reqIdRef.current !== myId || !d) return;
        // Perfil privado aprovado ao vivo (false→true): o endpoint leve não traz
        // o conteúdo travado — faz um load() completo pra destravar Biblioteca/Clubes.
        const prevData = dataRef.current;
        if (prevData && prevData.locked && d.isFollowing && !prevData.isFollowing) { load(); return; }
        const FIELDS = ['followers', 'following', 'pendingRequests', 'isFollowing', 'requested', 'followsYou', 'iBlocked'];
        setData((prev: any) => {
          if (!prev) return prev;
          const patch: any = {};
          for (const k of FIELDS) if (k in d) patch[k] = d[k];
          return { ...prev, ...patch };
        });
      })
      .catch(() => {});
  }, [session, target, followBusy, load]);
  useLiveRefresh(softRefresh, 20000, !!data);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; }, [tab]);
  useEffect(() => { if (data && data.isMe) { api.followingActivity(session).then((r: any) => setFriends(r.activity || [])).catch(() => setFriends([])); } }, [data && data.isMe, session]);
  // Meu Círculo: só pergunta quem o perfil já diz que é Mentor (is_mentor).
  const [meuCirculo, setMeuCirculo] = useState<any>(null);
  const comCirculo = !!(souMentor && onAbrirMeuCirculo && data && data.isMe);
  useEffect(() => {
    if (!comCirculo) { setMeuCirculo(null); return; }
    let vivo = true;
    api.getMeuCirculo(session).then((r: any) => { if (vivo) setMeuCirculo(r?.mentor || null); }).catch(() => {});
    return () => { vivo = false; };
  }, [comCirculo, session]);

  // stories ativos do dono do perfil (cruzando clubes visíveis ao viewer)
  const storiesCtx = useStories();
  const [activeStories, setActiveStories] = useState<any>(null);
  useEffect(() => {
    if (!data || data.locked || !data.id) { setActiveStories(null); return; }
    let alive = true;
    api.userStories(session, data.id).then((r: any) => { if (alive) setActiveStories(r.stories || []); }).catch(() => { if (alive) setActiveStories([]); });
    return () => { alive = false; };
  }, [data && data.id, data && data.locked, session]);
  const openStories = useCallback(() => {
    if (!activeStories || !activeStories.length || !storiesCtx) return;
    storiesCtx.openProfilePlayer(activeStories, {
      user: { id: data.id, name: data.name, avatarUrl: data.avatarUrl },
      onGoToEstudo: (clubId: string) => onGoToEstudo && onGoToEstudo(clubId),
    });
  }, [activeStories, data, storiesCtx, onGoToEstudo]);

  const doFollow = async () => {
    if (!data || followBusy) return;
    followLockRef.current = Date.now() + 12000; // trava o poll ANTES do await (síncrono)
    setFollowBusy(true);
    const wasFollowing = data.isFollowing, wasRequested = data.requested;
    try {
      if (wasFollowing || wasRequested) {
        const r = await api.unfollowUser(session, data.id);
        setData((d: any) => ({ ...d, isFollowing: false, requested: false, ...(r && r.counts ? { followers: r.counts.followers, following: r.counts.following } : {}) }));
      } else {
        const r = await api.followUser(session, data.id);
        const req = !!(r && r.requested);
        setData((d: any) => ({ ...d, isFollowing: !req, requested: req, ...(r && r.counts ? { followers: r.counts.followers, following: r.counts.following } : {}) }));
        if (!req && data.locked) load();
      }
    } catch (e: any) { window.alert(e?.message || 'Erro'); }
    finally { followLockRef.current = Date.now() + 12000; setFollowBusy(false); }
  };

  // CADERNOS NA FILEIRA DAS ABAS.
  //
  // Estava escondido DENTRO da Biblioteca — um chip ao lado das dimensões de
  // ordenação, que é o último lugar onde alguém procuraria as próprias
  // anotações. E pior: como as dimensões só existem com livro na estante,
  // quem não tinha livro não achava o caminho de jeito nenhum.
  //
  // `leva: true` diz que esta aba SAI da tela em vez de trocar o painel — é
  // por isso que ela nunca fica acesa, e por isso ganha uma seta.
  //
  // Só no próprio perfil: caderno de outra pessoa não é assunto de ninguém.
  const tabs = [
    { id: 'visao', label: 'Visão geral' },
    { id: 'biblioteca', label: 'Biblioteca' },
    ...(data && data.isMe && onAbrirEstudos ? [{ id: 'cadernos', label: 'Cadernos', leva: true }] : []),
    { id: 'clubes', label: 'Clubes' },
    { id: 'conquistas', label: 'Conquistas' },
  ];

  return (
    <div style={{ minHeight: '100%', background: 'var(--paper)', color: 'var(--ink)', ...THEME_ALIAS }}>
      <div ref={scrollRef} style={{ height: '100%', overflowY: 'auto' }}>
        {err ? (
          <div style={{ padding: '60px 26px', textAlign: 'center' }}>
            {onBack && <button onClick={onBack} style={{ position: 'absolute', top: 14, left: 14, width: 36, height: 36, borderRadius: 10, border: '1px solid var(--hair)', background: 'var(--card-2)', color: 'var(--cream)', display: 'grid', placeItems: 'center' }}><Icon name="back" size={18} /></button>}
            <div style={{ width: 60, height: 60, borderRadius: 18, background: 'var(--card-2)', display: 'grid', placeItems: 'center', margin: '0 auto 16px', color: 'var(--muted)' }}><Icon name="users" size={26} /></div>
            <h3 style={{ fontFamily: 'var(--serif)', fontSize: 22, color: 'var(--cream)', margin: '0 0 8px' }}>Perfil indisponível</h3>
            <p style={{ fontSize: 14, color: 'var(--muted)', margin: 0 }}>{err}</p>
            <div style={{ marginTop: 18 }}><BtnGhost onClick={load}>Tentar de novo</BtnGhost></div>
          </div>
        ) : !data ? <LoadingView /> : (
          <>
            {comCirculo && meuCirculo && <MeuCirculo meu={meuCirculo} onAbrir={onAbrirMeuCirculo} onVerPagina={() => onVerMinhaPagina && onVerMinhaPagina(meuCirculo.slug)} />}
            <ProfileHeader data={data} onBack={onBack} followBusy={followBusy}
              stories={activeStories} onOpenStories={openStories}
              onOpenPeople={(tb: string) => setPeople(tb)} onOpenSettings={onOpenSettings}
              onOpenMore={() => setMore(true)} onOpenRequests={() => setRequests(true)} onEdit={() => setEdit(true)} onShare={() => setShare(true)} onFollow={doFollow} />
            <TabBar tabs={tabs} active={tab} onChange={(id: string) => {
              if (id === 'cadernos') { if (onAbrirEstudos) onAbrirEstudos(); return; }
              setTab(id);
            }} />
            <div style={{ padding: '0 18px calc(108px + env(safe-area-inset-bottom, 0px))' }}>
              {tab === 'visao' && <OverviewTab data={data} friends={friends} onTab={setTab} onOpenClub={onOpenClub} onOpenProfile={onOpenProfile} onUpgrade={onUpgrade} onFollow={doFollow} onOpenBook={onOpenBook} />}
              {tab === 'biblioteca' && <LibraryTab data={data} isMe={data.isMe} onAddBook={onAddBook} onFollow={doFollow} onOpenBook={onOpenBook} myBooks={myBooks} myShelves={myShelves} estantes={estantes}  onAbrirEstudos={onAbrirEstudos}/>}
              {tab === 'clubes' && <ClubsTab data={data} isMe={data.isMe} onOpenClub={onOpenClub} onExploreClubs={onExploreClubs} onFollow={doFollow} />}
              {tab === 'conquistas' && <AchievementsTab data={data} onFollow={doFollow} />}
            </div>
          </>
        )}
      </div>

      {data && people && <PeopleSheet session={session} profile={data} tab={people} onClose={() => setPeople(null)} onOpenProfile={onOpenProfile} onMutate={() => { followLockRef.current = Date.now() + 12000; }} onFollowDelta={(delta: number) => { if (data.isMe) setData((d: any) => ({ ...d, following: Math.max(0, (d.following || 0) + delta) })); }} />}
      {data && share && <ShareSheet profile={data} onClose={() => setShare(false)} />}
      {data && more && !data.isMe && <MoreSheet session={session} profile={data} onClose={() => setMore(false)} onBlocked={onBack} />}
      {data && edit && data.isMe && <EditProfileSheet session={session} data={data} onClose={() => setEdit(false)} onSaved={(patch: any) => { setData((d: any) => ({ ...d, ...patch })); onProfileUpdated && onProfileUpdated(); }} />}
      {data && requests && data.isMe && <RequestsSheet session={session} onClose={() => setRequests(false)} onOpenProfile={onOpenProfile} onMutate={() => { followLockRef.current = Date.now() + 12000; }} onResolved={(approved: boolean) => setData((d: any) => ({ ...d, pendingRequests: Math.max(0, (d.pendingRequests || 0) - 1), followers: approved ? (d.followers || 0) + 1 : d.followers }))} />}
    </div>
  );
};

// Wrapper: provê o contexto de stories (modo perfil, sem clube) pro anel + player.
export const ProfileSocialScreen: React.FC<any> = (props) => (
  <StoriesProvider session={props.session} viewer={{ id: props.currentUserId }} clubId={null} isElevated={false}>
    <ProfileSocialInner {...props} />
    <StoriesOverlays />
  </StoriesProvider>
);

// As folhas do perfil fora desta tela (Perfil da interface nova, 31/10/2026):
// editar, compartilhar e seguidores/seguindo, com os mesmos aliases de tema e
// as mesmas animações. `data` é o mesmo de GET /api/users/:id.
export function FolhaDoPerfil({ tipo, session, data, pessoas, onClose, onSaved, onOpenProfile, onFollowDelta }: any) {
  useEffect(() => { injectCss(); }, []);
  // O voltar do aparelho/navegador fecha a folha (e não a tela de baixo).
  const fecharRef = useRef(onClose);
  fecharRef.current = onClose;
  useEffect(() => aoVoltarFechar(() => fecharRef.current?.()), []);
  return (
    <div style={{ ...THEME_ALIAS, position: 'relative', zIndex: 9600 }} data-folha-perfil={tipo}>
      {tipo === 'editar' && <EditProfileSheet session={session} data={data} onClose={onClose} onSaved={onSaved} />}
      {tipo === 'compartilhar' && <ShareSheet profile={data} onClose={onClose} />}
      {tipo === 'pessoas' && <PeopleSheet session={session} profile={data} tab={pessoas} onClose={onClose} onOpenProfile={onOpenProfile} onMutate={() => {}} onFollowDelta={onFollowDelta} />}
    </div>
  );
}
